import { useMemo, useState, useEffect, useRef, type ChangeEvent } from 'react';
import {
  Droplets,
  Printer,
  Save,
  Trash2,
  History,
  X,
  GripVertical,
  Copy,
  CopyPlus,
} from 'lucide-react';

type TipoPintura = 'G' | 'B' | 'A';

interface TempoCorteItem {
  id: string;
  descricao: string;
  qtd: number;
  medida: string;
  metrosQuadrados?: number;
  material?: string;
}

interface ProdutoTempoCorteSalvo {
  id: string;
  nome: string;
  lote?: string;
  itens?: TempoCorteItem[];
}

interface LoteProdutoRow {
  lote: string;
  produtoNome: string;
  qtd: number;
}

interface Props {
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[];
  lotesProdutos: LoteProdutoRow[];
}

const STORAGE_KEY = 'boletim-linha-pintura-v4';
const HISTORICO_KEY = 'boletim-linha-pintura-historico-v1';
const LOGO_WHATSAPP_PNG_KEY = 'boletim-linha-pintura-logo-whatsapp-v1';

/** Estilos inline para html2canvas reproduzirem bordas e cabeçalho como na impressão. */
const PNG_TH =
  'border:1px solid #334155;padding:4px 6px;vertical-align:top;background-color:#e0f2fe;font-size:8.5pt;font-weight:600;color:#0f172a;';
const PNG_TD =
  'border:1px solid #334155;padding:4px 6px;vertical-align:top;color:#0f172a;font-size:9pt;word-break:break-word;overflow-wrap:anywhere;';
const PNG_TD_C = `${PNG_TD};text-align:center`;
const PNG_TABLE =
  'width:100%;border-collapse:collapse;table-layout:fixed;border:1px solid #334155;background:#fff;';
const GAP_SEG_ENTRE_PECAS = 35;

const TOKENS_COR_INICIO = [
  'OFF/CEDRO/RIPADO',
  'OFF/CEDRO',
  'CEDRO/OFF',
  'OFF/NATURALE',
  'PRETO/CEDRO',
  'CEDRO/PRETO',
  'OFF WHITE',
  'RIPADO 3D',
  'BRANCO',
  'PRETO',
  'CEDRO',
  'RIPADO',
  'NATURALE',
  'OUTROS',
  'WHITE',
  'OFF',
];

/** Frases compostas removidas de qualquer posição */
const FRASES_COR = [
  'OFF WHITE',
  'RIPADO 3D',
  'OFF/CEDRO/RIPADO',
  'OFF/CEDRO',
  'CEDRO/OFF',
  'OFF/NATURALE',
  'PRETO/CEDRO',
  'CEDRO/PRETO',
  'PRETO/CEDRO',
];

/** Palavras de cor removidas (descrição sem cor em qualquer posição) */
const PALAVRAS_COR = new Set([
  'BRANCO',
  'PRETO',
  'CEDRO',
  'OFF',
  'WHITE',
  'NATURALE',
  'RIPADO',
  'OUTROS',
  'FREIJO',
  'FENDI',
  'NATURAL',
  '3D',
]);

function limparCorDoInicio(desc: string): string {
  const original = String(desc || '').trim();
  if (!original) return original;
  let s = original;
  let guard = 0;
  while (guard++ < 40) {
    const upper = s.toUpperCase();
    let stripped = false;
    const ordenados = [...TOKENS_COR_INICIO].sort((a, b) => b.length - a.length);
    for (const token of ordenados) {
      const t = token.toUpperCase();
      if (upper.startsWith(t + ' ') || upper.startsWith(t + '/') || upper === t) {
        s = s.slice(token.length).replace(/^[\s/]+/, '').trim();
        stripped = true;
        break;
      }
    }
    if (!stripped) break;
  }
  return s.trim() || original;
}

/** Remove cores do início, frases e palavras soltas (ex.: LATERAL DIR CEDRO → LATERAL DIR) */
function limparDescricaoCompleta(raw: string): string {
  let s = limparCorDoInicio(raw);
  const frases = [...FRASES_COR].sort((a, b) => b.length - a.length);
  for (const f of frases) {
    const esc = f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    s = s.replace(new RegExp(esc, 'gi'), ' ');
  }
  s = s.replace(/\s+/g, ' ').trim();
  const words = s.split(/\s+/).filter((w) => {
    const u = w.toUpperCase().replace(/[^A-ZÀ-Ú0-9/]/g, '');
    if (!u) return false;
    if (PALAVRAS_COR.has(u)) return false;
    if (u.includes('/') && ['OFF', 'CEDRO', 'PRETO', 'BRANCO'].some((c) => u.split('/').includes(c))) return false;
    return true;
  });
  const out = words.join(' ').trim();
  return out || String(raw || '').trim();
}

function normalizar(s: string) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function parseMedidaMm(medida?: string): { largura: number; altura: number } {
  const m = String(medida || '').toLowerCase().match(/(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)/);
  if (!m) return { largura: 0, altura: 0 };
  const largura = Number(m[1].replace(',', '.')) || 0;
  const altura = Number(m[2].replace(',', '.')) || 0;
  return { largura, altura };
}

function maiorLadoMm(medida?: string): number {
  const { largura, altura } = parseMedidaMm(medida);
  return Math.max(largura || 0, altura || 0);
}

function metroQuadradoUnidade(it: TempoCorteItem): number {
  const m = Number(it.metrosQuadrados || 0);
  if (m > 0) return m;
  const { largura, altura } = parseMedidaMm(it.medida);
  return (largura * altura) / 1_000_000;
}

function formatarSegundosHHMMSS(segundos: number) {
  const t = Math.max(0, Math.round(segundos || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Exibe só horário (início/término no mesmo dia) */
function formatarHoraBR(d: Date) {
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function escapeHtml(s: string) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatarHHMMSS(d: Date) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(
    d.getSeconds()
  ).padStart(2, '0')}`;
}

/** Converte valor legado (datetime-local) para HH:MM:SS do input type="time" */
function normalizarHorarioArmazenado(valor: string): string {
  const s = String(valor || '').trim();
  if (!s) return '';
  if (s.includes('T')) {
    const part = s.split('T')[1] || '';
    const hhmmss = part.slice(0, 8);
    const hhmm = part.slice(0, 5);
    return hhmmss.length === 8 ? hhmmss : `${hhmm}:00`;
  }
  if (s.length >= 8) return s.slice(0, 8);
  if (s.length >= 5) return `${s.slice(0, 5)}:00`;
  return s;
}

function qtdProdutosNoLote(lotesProdutos: LoteProdutoRow[], loteRef: string, nomeProduto: string): number {
  const loteN = normalizar(loteRef);
  const nomeN = normalizar(nomeProduto);
  if (!loteN || !nomeN) return 0;
  return lotesProdutos
    .filter((lp) => normalizar(lp.lote) === loteN && normalizar(lp.produtoNome) === nomeN)
    .reduce((s, lp) => s + (Number(lp.qtd) || 0), 0);
}

function chavePeca(produtoId: string, itemId: string) {
  return `${produtoId}::${itemId}`;
}

function novoIdBloco() {
  return `b-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function novoIdInstancia() {
  return `i-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Normaliza bloco vindo do armazenamento (legado: qtdPorCor único) */
function migrateBlocoForLoad(
  b: Partial<BlocoLinhaPintura> & { qtdPorCor?: number; id?: string }
): BlocoLinhaPintura {
  const ids = Array.isArray(b.produtoIds) ? b.produtoIds.map(String) : [];
  return {
    id: b.id || novoIdBloco(),
    tituloProdutoCor: String(b.tituloProdutoCor || ''),
    corLote: typeof (b as { corLote?: string }).corLote === 'string' ? String((b as { corLote?: string }).corLote) : '',
    produtoIds: ids,
    descricaoOverridePorRowKey:
      (b as { descricaoOverridePorRowKey?: unknown }).descricaoOverridePorRowKey &&
      typeof (b as { descricaoOverridePorRowKey?: unknown }).descricaoOverridePorRowKey === 'object'
        ? ((b as { descricaoOverridePorRowKey: Record<string, string> }).descricaoOverridePorRowKey ?? {})
        : {},
    descricaoOverridePorInstanciaId:
      (b as { descricaoOverridePorInstanciaId?: unknown }).descricaoOverridePorInstanciaId &&
      typeof (b as { descricaoOverridePorInstanciaId?: unknown }).descricaoOverridePorInstanciaId === 'object'
        ? ((b as { descricaoOverridePorInstanciaId: Record<string, string> }).descricaoOverridePorInstanciaId ?? {})
        : {},
    corPorRowKey:
      (b as { corPorRowKey?: unknown }).corPorRowKey && typeof (b as { corPorRowKey?: unknown }).corPorRowKey === 'object'
        ? ((b as { corPorRowKey: Record<string, string> }).corPorRowKey ?? {})
        : {},
    qtdCorOverridePorRowKey:
      (b as { qtdCorOverridePorRowKey?: unknown }).qtdCorOverridePorRowKey &&
      typeof (b as { qtdCorOverridePorRowKey?: unknown }).qtdCorOverridePorRowKey === 'object'
        ? ((b as { qtdCorOverridePorRowKey: Record<string, number> }).qtdCorOverridePorRowKey ?? {})
        : {},
    corPorInstanciaId:
      (b as { corPorInstanciaId?: unknown }).corPorInstanciaId &&
      typeof (b as { corPorInstanciaId?: unknown }).corPorInstanciaId === 'object'
        ? ((b as { corPorInstanciaId: Record<string, string> }).corPorInstanciaId ?? {})
        : {},
    qtdCorOverridePorInstanciaId:
      (b as { qtdCorOverridePorInstanciaId?: unknown }).qtdCorOverridePorInstanciaId &&
      typeof (b as { qtdCorOverridePorInstanciaId?: unknown }).qtdCorOverridePorInstanciaId === 'object'
        ? ((b as { qtdCorOverridePorInstanciaId: Record<string, number> }).qtdCorOverridePorInstanciaId ?? {})
        : {},
    cabePorPeca: b.cabePorPeca && typeof b.cabePorPeca === 'object' ? b.cabePorPeca : {},
    tipoPinturaPorPeca: b.tipoPinturaPorPeca && typeof b.tipoPinturaPorPeca === 'object' ? b.tipoPinturaPorPeca : {},
    tipoPinturaPorInstanciaId:
      (b as { tipoPinturaPorInstanciaId?: unknown }).tipoPinturaPorInstanciaId &&
      typeof (b as { tipoPinturaPorInstanciaId?: unknown }).tipoPinturaPorInstanciaId === 'object'
        ? ((b as { tipoPinturaPorInstanciaId: Record<string, TipoPintura> }).tipoPinturaPorInstanciaId ?? {})
        : {},
    rodapeInicioHora: typeof (b as { rodapeInicioHora?: unknown }).rodapeInicioHora === 'string' ? String((b as { rodapeInicioHora?: string }).rodapeInicioHora) : '',
    rodapeInicioData: typeof (b as { rodapeInicioData?: unknown }).rodapeInicioData === 'string' ? String((b as { rodapeInicioData?: string }).rodapeInicioData) : '',
    rodapeTerminoHora: typeof (b as { rodapeTerminoHora?: unknown }).rodapeTerminoHora === 'string' ? String((b as { rodapeTerminoHora?: string }).rodapeTerminoHora) : '',
    rodapeTerminoData: typeof (b as { rodapeTerminoData?: unknown }).rodapeTerminoData === 'string' ? String((b as { rodapeTerminoData?: string }).rodapeTerminoData) : '',
    excluirInstanciaIds: (() => {
      const ext = b as { excluirInstanciaIds?: string[]; excluirRowKeys?: string[] };
      if (Array.isArray(ext.excluirInstanciaIds) && ext.excluirInstanciaIds.length > 0) {
        return ext.excluirInstanciaIds.map(String);
      }
      if (Array.isArray(ext.excluirRowKeys) && ext.excluirRowKeys.length > 0) {
        return ext.excluirRowKeys.map(String);
      }
      return [];
    })(),
    ordemInstancias: (() => {
      const ext = b as {
        ordemInstancias?: Array<{ id?: string; rowKey: string }>;
        ordemRowKeys?: string[];
      };
      if (Array.isArray(ext.ordemInstancias) && ext.ordemInstancias.length > 0) {
        return ext.ordemInstancias
          .filter((o) => o && typeof o.rowKey === 'string')
          .map((o) => ({
            id: typeof o.id === 'string' && o.id ? o.id : o.rowKey,
            rowKey: o.rowKey,
          }));
      }
      if (Array.isArray(ext.ordemRowKeys) && ext.ordemRowKeys.length > 0) {
        return ext.ordemRowKeys.map((k) => ({ id: k, rowKey: k }));
      }
      return [];
    })(),
    horarioInicioPrimeira: normalizarHorarioArmazenado(
      typeof b.horarioInicioPrimeira === 'string' ? b.horarioInicioPrimeira : ''
    ),
  };
}

function rowKeyPertenceAAlgumProduto(rowKey: string, pids: string[]): boolean {
  return pids.some((pid) => rowKey.startsWith(`m::${pid}|`));
}

function instanciaIdPareceRowKeyDeProduto(id: string, pids: string[]): boolean {
  return id.startsWith('m::') && rowKeyPertenceAAlgumProduto(id, pids);
}

/** Extrai do bloco salvo no histórico as linhas dos produtos indicados (mesma cor / mesmo resumo). */
function filtrarBlocoDoHistoricoParaProdutos(
  pids: string[],
  base: BlocoLinhaPintura,
  tituloFallback: string
): BlocoLinhaPintura {
  const pidsUnique = [...new Set(pids.map(String))];
  if (pidsUnique.length === 0) {
    return migrateBlocoForLoad({
      ...base,
      id: novoIdBloco(),
      produtoIds: [],
      tituloProdutoCor: tituloFallback,
      excluirInstanciaIds: [],
      ordemInstancias: [],
      horarioInicioPrimeira: '',
      rodapeInicioHora: '',
      rodapeInicioData: '',
      rodapeTerminoHora: '',
      rodapeTerminoData: '',
    });
  }

  const filtraKey = (k: string) => rowKeyPertenceAAlgumProduto(k, pidsUnique);

  const filterRowKeyMaps = <T extends Record<string, unknown>>(obj: T | undefined): T => {
    if (!obj) return {} as T;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (filtraKey(k)) out[k] = v;
    }
    return out as T;
  };

  const filtrarMapInstanciaPorPrefixoRowKey = <T,>(m: Record<string, T> | undefined): Record<string, T> => {
    const out: Record<string, T> = {};
    if (!m) return out;
    for (const [k, v] of Object.entries(m)) {
      if (instanciaIdPareceRowKeyDeProduto(k, pidsUnique)) out[k] = v;
    }
    return out;
  };

  const limparHorarioRodape: Partial<BlocoLinhaPintura> = {
    horarioInicioPrimeira: '',
    rodapeInicioHora: '',
    rodapeInicioData: '',
    rodapeTerminoHora: '',
    rodapeTerminoData: '',
  };

  const ordemFiltrada = (base.ordemInstancias || []).filter((o) => filtraKey(o.rowKey));

  if (ordemFiltrada.length === 0) {
    const excluirFiltrado = (base.excluirInstanciaIds || []).filter((id) => instanciaIdPareceRowKeyDeProduto(id, pidsUnique));
    return {
      ...base,
      id: novoIdBloco(),
      produtoIds: pidsUnique,
      tituloProdutoCor: base.tituloProdutoCor || tituloFallback,
      excluirInstanciaIds: excluirFiltrado,
      ordemInstancias: [],
      cabePorPeca: filterRowKeyMaps(base.cabePorPeca),
      tipoPinturaPorPeca: filterRowKeyMaps(base.tipoPinturaPorPeca),
      descricaoOverridePorRowKey: filterRowKeyMaps(base.descricaoOverridePorRowKey),
      corPorRowKey: filterRowKeyMaps(base.corPorRowKey),
      qtdCorOverridePorRowKey: filterRowKeyMaps(base.qtdCorOverridePorRowKey),
      corPorInstanciaId: filtrarMapInstanciaPorPrefixoRowKey(base.corPorInstanciaId),
      qtdCorOverridePorInstanciaId: filtrarMapInstanciaPorPrefixoRowKey(base.qtdCorOverridePorInstanciaId),
      descricaoOverridePorInstanciaId: filtrarMapInstanciaPorPrefixoRowKey(base.descricaoOverridePorInstanciaId),
      tipoPinturaPorInstanciaId: filtrarMapInstanciaPorPrefixoRowKey(base.tipoPinturaPorInstanciaId),
      ...limparHorarioRodape,
    };
  }

  const idMap = new Map<string, string>();
  for (const o of ordemFiltrada) {
    idMap.set(o.id, novoIdInstancia());
  }

  const novaOrdem = ordemFiltrada.map((o) => ({
    id: idMap.get(o.id)!,
    rowKey: o.rowKey,
  }));

  const remapInst = <T,>(m: Record<string, T> | undefined): Record<string, T> => {
    const out: Record<string, T> = {};
    if (!m) return out;
    for (const [k, v] of Object.entries(m)) {
      const nk = idMap.get(k);
      if (nk) out[nk] = v;
    }
    return out;
  };

  const excluirRemapeado = (base.excluirInstanciaIds || [])
    .map((id) => idMap.get(id))
    .filter(Boolean) as string[];

  return {
    ...base,
    id: novoIdBloco(),
    produtoIds: pidsUnique,
    tituloProdutoCor: base.tituloProdutoCor || tituloFallback,
    excluirInstanciaIds: excluirRemapeado,
    ordemInstancias: novaOrdem,
    cabePorPeca: filterRowKeyMaps(base.cabePorPeca),
    tipoPinturaPorPeca: filterRowKeyMaps(base.tipoPinturaPorPeca),
    descricaoOverridePorRowKey: filterRowKeyMaps(base.descricaoOverridePorRowKey),
    corPorRowKey: filterRowKeyMaps(base.corPorRowKey),
    qtdCorOverridePorRowKey: filterRowKeyMaps(base.qtdCorOverridePorRowKey),
    corPorInstanciaId: remapInst(base.corPorInstanciaId),
    qtdCorOverridePorInstanciaId: remapInst(base.qtdCorOverridePorInstanciaId),
    descricaoOverridePorInstanciaId: remapInst(base.descricaoOverridePorInstanciaId),
    tipoPinturaPorInstanciaId: remapInst(base.tipoPinturaPorInstanciaId),
    ...limparHorarioRodape,
  };
}

function filtrarBlocoDoHistoricoParaProduto(pid: string, base: BlocoLinhaPintura, tituloFallback: string): BlocoLinhaPintura {
  return filtrarBlocoDoHistoricoParaProdutos([pid], base, tituloFallback);
}

interface LinhaCalculada {
  rowKey: string;
  produtoId: string;
  nomeProduto: string;
  descricao: string;
  material: string;
  medida: string;
  qtd: number;
  cabe: number;
  tempoSegundos: number;
  tipoPintura: TipoPintura;
}

interface BlocoLinhaPintura {
  id: string;
  tituloProdutoCor: string;
  /** Cor do lote usada para buscar quantidade (ex.: OFF, CEDRO). */
  corLote: string;
  produtoIds: string[];
  /** Permite ajustar o texto mostrado na coluna Descrição (por linha). */
  descricaoOverridePorRowKey?: Record<string, string>;
  /** Overrides por instância (linhas duplicadas não compartilham). */
  descricaoOverridePorInstanciaId?: Record<string, string>;
  /** Cor por instância (opcional). */
  corPorInstanciaId?: Record<string, string>;
  /** Quantidade por cor por instância (opcional). */
  qtdCorOverridePorInstanciaId?: Record<string, number>;
  /** Legado / mesclagem: cor por rowKey antes de instâncias. */
  corPorRowKey?: Record<string, string>;
  qtdCorOverridePorRowKey?: Record<string, number>;
  cabePorPeca: Record<string, number>;
  tipoPinturaPorPeca: Record<string, TipoPintura>;
  /** Tipo por instância (linhas duplicadas não compartilham). */
  tipoPinturaPorInstanciaId?: Record<string, TipoPintura>;
  /** Rodapé (preenchido na tela para imprimir). */
  rodapeInicioHora?: string;
  rodapeInicioData?: string;
  rodapeTerminoHora?: string;
  rodapeTerminoData?: string;
  /** Instâncias excluídas (id único por linha na tabela; duplicatas têm ids diferentes) */
  excluirInstanciaIds: string[];
  /** Ordem de todas as instâncias (inclui excluídas no lugar); vazio = ordem alfabética uma por rowKey */
  ordemInstancias: Array<{ id: string; rowKey: string }>;
  /** Apenas horário HH:MM (ou legado datetime-local) */
  horarioInicioPrimeira: string;
}

interface RelatorioSalvo {
  id: string;
  nome: string;
  criadoEmISO: string;
  loteReferencia: string;
  blocos?: BlocoLinhaPintura[];
  metrosPor60Seg: number;
  qtdPorCorLote?: Record<string, number>;
  /** legado */
  produtoIds?: string[];
  cabePorPeca?: Record<string, number>;
  tipoPinturaPorProduto?: Record<string, TipoPintura>;
  horarioInicioPrimeira?: string;
}

/** Resumo no histórico = bloco com corLote + conjunto de produtos. Para automático: filtrar pela cor pedida e pelo lote. */
function pontuacaoBlocoModeloParaCorDoLote(
  b: BlocoLinhaPintura,
  idsLote: Set<string>,
  corAlvoNorm: string,
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[]
): number {
  const idsB = (b.produtoIds || []).map(String);
  const inter = idsB.filter((id) => idsLote.has(id));
  if (inter.length === 0) return -1;

  const corB = normalizar(b.corLote || '');
  const tit = normalizar(b.tituloProdutoCor || '');

  if (corAlvoNorm) {
    const bateCor = corB === corAlvoNorm;
    const bateTitulo = tit.includes(corAlvoNorm);
    if (!bateCor && !bateTitulo) return -1;
  }

  let score = 0;
  if (corAlvoNorm) {
    if (corB === corAlvoNorm) score += 400;
    else if (tit.includes(corAlvoNorm)) score += 260;
  }

  const setB = new Set(idsB);
  if (setB.size === idsLote.size && [...setB].every((x) => idsLote.has(x))) {
    score += 220;
  } else if ([...idsLote].every((x) => setB.has(x))) {
    score += 140;
  } else if ([...setB].every((x) => idsLote.has(x))) {
    score += 90;
  }

  score += Math.min(inter.length, 8) * 12;

  const nomesResumo = [...setB]
    .map((id) => normalizar(produtosTempoCorteSalvos.find((p) => p.id === id)?.nome || ''))
    .filter(Boolean);
  const resumoText = nomesResumo.join(' ');
  for (const id of idsLote) {
    const nn = normalizar(produtosTempoCorteSalvos.find((p) => p.id === id)?.nome || '');
    if (nn && resumoText.includes(nn)) score += 15;
  }

  return score;
}

/** Escolhe o resumo (bloco) do histórico cuja cor bate com `corAlvo` (OFF, CEDRO…) e produtos com o lote. */
function encontrarBlocoModeloHistoricoParaCorDoLote(
  corAlvo: string,
  idsLote: string[],
  historico: RelatorioSalvo[],
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[]
): BlocoLinhaPintura | undefined {
  const corN = normalizar(corAlvo);
  const setLote = new Set(idsLote.map(String));

  const cands: { bloco: BlocoLinhaPintura; score: number; criadoEm: number }[] = [];

  for (const h of historico) {
    const bruto = h.blocos && h.blocos.length > 0 ? h.blocos : [];
    if (bruto.length === 0) continue;
    const criadoEm = new Date(h.criadoEmISO).getTime();
    for (const raw of bruto) {
      const b = migrateBlocoForLoad(raw as Partial<BlocoLinhaPintura> & { qtdPorCor?: number });
      const sc = pontuacaoBlocoModeloParaCorDoLote(b, setLote, corN, produtosTempoCorteSalvos);
      if (sc >= 0) cands.push({ bloco: b, score: sc, criadoEm });
    }
  }

  cands.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return b.criadoEm - a.criadoEm;
  });
  return cands[0]?.bloco;
}

function calcularLinhasBloco(
  bloco: BlocoLinhaPintura,
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[],
  lotesProdutos: LoteProdutoRow[],
  loteReferencia: string,
  metrosPor60Seg: number,
  qtdPorCorLote: Record<string, number>
): LinhaCalculada[] {
  const m60 = Number(metrosPor60Seg) || 17;
  const raw: Array<{
    mergeKey: string;
    rowKey: string;
    produtoId: string;
    nomeProduto: string;
    descricao: string;
    material: string;
    medida: string;
    qtd: number;
    cabe: number;
    tipo: TipoPintura;
  }> = [];

  const getCabe = (k: string, map: Record<string, number>) => {
    const v = map[k];
    return v != null && v > 0 ? v : 1;
  };
  const getTipo = (k: string, map: Record<string, TipoPintura>): TipoPintura => map[k] ?? 'G';

  for (const pid of bloco.produtoIds) {
    const prod = produtosTempoCorteSalvos.find((p) => p.id === pid);
    if (!prod) continue;
    const nomeP = prod.nome || '';
    const itens = Array.isArray(prod.itens) ? prod.itens : [];

    for (const it of itens) {
      const descL = limparDescricaoCompleta(it.descricao || '-');
      const med = String(it.medida || '-').trim();
      const mat = String(it.material || '-').trim();
      const mergeKey = `${pid}|${normalizar(descL)}|${normalizar(med)}|${normalizar(mat)}`;
      const rk = `m::${mergeKey}`;
      const descOverride = bloco.descricaoOverridePorRowKey?.[rk];
      const descFinal = String(descOverride || '').trim() ? String(descOverride).trim() : descL;
      const cabe = getCabe(rk, bloco.cabePorPeca);
      const tipo = getTipo(rk, bloco.tipoPinturaPorPeca);
      const qtdPeca = Math.round(Number(it.qtd) || 0);

      raw.push({
        mergeKey,
        rowKey: rk,
        produtoId: prod.id,
        nomeProduto: nomeP,
        descricao: descFinal,
        material: mat,
        medida: med,
        qtd: qtdPeca,
        cabe,
        tipo,
      });
    }
  }

  const grupos = new Map<string, typeof raw>();
  for (const r of raw) {
    if (!grupos.has(r.mergeKey)) grupos.set(r.mergeKey, []);
    grupos.get(r.mergeKey)!.push(r);
  }

  const merged: LinhaCalculada[] = [];
  for (const [, lista] of grupos) {
    const first = lista[0];
    const qtdSum = lista.reduce((s, x) => s + x.qtd, 0);
    const { produtoId, nomeProduto, descricao, material, medida, cabe, tipo } = first;
    const rowKey = first.rowKey;
    const corLinha = bloco.corPorRowKey?.[rowKey] ?? bloco.corLote ?? '';
    const corKey = normalizar(corLinha);
    const qtdOverride = Number(bloco.qtdCorOverridePorRowKey?.[rowKey] ?? 0);
    const qtdCor =
      qtdOverride > 0 ? Math.floor(qtdOverride) : Math.max(0, Math.floor(Number(qtdPorCorLote[corKey]) || 0));
    const ladoMaior = maiorLadoMm(medida);
    const metragemLinearTotal = ladoMaior > 0 ? ladoMaior * qtdSum * qtdCor : 0;
    const metrosAPassar = cabe > 0 ? metragemLinearTotal / cabe : 0;
    const tempoSegundos = m60 > 0 ? (metrosAPassar * 60) / m60 : 0;
    merged.push({
      rowKey: first.rowKey,
      produtoId,
      nomeProduto,
      descricao,
      material,
      medida,
      qtd: qtdSum,
      cabe,
      tempoSegundos,
      tipoPintura: tipo,
    });
  }

  return merged.sort((a, b) => {
    const pa = a.nomeProduto.localeCompare(b.nomeProduto);
    if (pa !== 0) return pa;
    return a.descricao.localeCompare(b.descricao);
  });
}

function ordenacaoPadraoLinhas(a: LinhaCalculada, b: LinhaCalculada) {
  const pa = a.nomeProduto.localeCompare(b.nomeProduto);
  if (pa !== 0) return pa;
  return a.descricao.localeCompare(b.descricao);
}

type InstanciaLinha = { instanciaId: string; linha: LinhaCalculada };

/** Monta a sequência de instâncias (permite o mesmo rowKey repetido = cópias). */
function expandirInstanciasOrdem(
  linhas: LinhaCalculada[],
  ordemInstancias: Array<{ id: string; rowKey: string }> | undefined
): InstanciaLinha[] {
  const map = new Map(linhas.map((l) => [l.rowKey, l]));
  if (!ordemInstancias || ordemInstancias.length === 0) {
    return [...linhas].sort(ordenacaoPadraoLinhas).map((l) => ({ instanciaId: l.rowKey, linha: l }));
  }
  const out: InstanciaLinha[] = [];
  const rowKeysPedidos = new Set(ordemInstancias.map((o) => o.rowKey));
  for (const { id, rowKey } of ordemInstancias) {
    const linha = map.get(rowKey);
    if (linha) out.push({ instanciaId: id, linha });
  }
  const rest = linhas.filter((l) => !rowKeysPedidos.has(l.rowKey)).sort(ordenacaoPadraoLinhas);
  for (const l of rest) {
    out.push({ instanciaId: l.rowKey, linha: l });
  }
  return out;
}

function moverRowKeyNaLista(keys: string[], from: number, to: number): string[] {
  if (from === to || from < 0 || to < 0 || from >= keys.length || to >= keys.length) return keys;
  const copy = [...keys];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

/** Mantém excluídas no lugar; só reordena as instâncias visíveis. */
function mergeOrdemAposArrastar(
  sequenciaCompleta: InstanciaLinha[],
  excluirIds: Set<string>,
  visiveisReordenados: InstanciaLinha[]
): Array<{ id: string; rowKey: string }> {
  const visQueue = [...visiveisReordenados];
  const out: Array<{ id: string; rowKey: string }> = [];
  for (const s of sequenciaCompleta) {
    if (excluirIds.has(s.instanciaId)) {
      out.push({ id: s.instanciaId, rowKey: s.linha.rowKey });
    } else {
      const next = visQueue.shift();
      if (next) out.push({ id: next.instanciaId, rowKey: next.linha.rowKey });
    }
  }
  return out;
}

/** Aceita só hora (HH:MM) no dia atual, ou legado datetime-local */
function parseHorarioInicio(valor: string): Date | null {
  const v = valor.trim();
  if (!v) return null;
  if (v.includes('T')) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  const m = v.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const d = new Date();
  d.setSeconds(0, 0);
  d.setHours(Number(m[1]), Number(m[2]), m[3] != null ? Number(m[3]) : 0, 0);
  return d;
}

/** Término = início + tempo da peça; próxima peça após intervalo (GAP). */
function aplicarHorarios(
  sequencia: InstanciaLinha[],
  excluirIds: Set<string>,
  horarioInicioPrimeira: string
): Array<LinhaCalculada & { instanciaId: string; inicio: Date | null; fim: Date | null }> {
  const visiveis = sequencia.filter((s) => !excluirIds.has(s.instanciaId));
  const t0 = parseHorarioInicio(horarioInicioPrimeira);
  const valido = !!t0;

  if (!valido || !t0) {
    return visiveis.map((s) => ({
      ...s.linha,
      instanciaId: s.instanciaId,
      inicio: null as Date | null,
      fim: null as Date | null,
    }));
  }
  let cursor = t0.getTime();
  return visiveis.map((s, idx) => {
    const l = s.linha;
    const dur = Math.max(0, l.tempoSegundos);
    const inicio = new Date(cursor);
    const fim = new Date(cursor + dur * 1000);
    const row = { ...l, instanciaId: s.instanciaId, inicio, fim };
    cursor = fim.getTime() + (idx < visiveis.length - 1 ? GAP_SEG_ENTRE_PECAS * 1000 : 0);
    return row;
  });
}

function descricaoInstanciaParaRelatorio(
  bloco: BlocoLinhaPintura,
  instanciaId: string,
  fallback: string
): string {
  const ov = bloco.descricaoOverridePorInstanciaId;
  if (ov && Object.prototype.hasOwnProperty.call(ov, instanciaId)) {
    return String(ov[instanciaId] ?? '');
  }
  return fallback;
}

function sequenciaComDuracoesParaRelatorio(
  bloco: BlocoLinhaPintura,
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[],
  lotesProdutos: LoteProdutoRow[],
  loteReferencia: string,
  metrosPor60Seg: number,
  qtdPorCorLote: Record<string, number>
): InstanciaLinha[] {
  const m60 = Number(metrosPor60Seg) || 17;
  const linhasBase = calcularLinhasBloco(bloco, produtosTempoCorteSalvos, lotesProdutos, loteReferencia, metrosPor60Seg, qtdPorCorLote);
  const seq = expandirInstanciasOrdem(linhasBase, bloco.ordemInstancias.length > 0 ? bloco.ordemInstancias : undefined);
  return seq.map((s) => {
    const id = s.instanciaId;
    const corLinha = bloco.corPorInstanciaId?.[id] ?? bloco.corLote ?? '';
    const corKey = normalizar(corLinha);
    const qtdOverride = Number(bloco.qtdCorOverridePorInstanciaId?.[id] ?? 0);
    const qtdCor =
      qtdOverride > 0 ? Math.floor(qtdOverride) : Math.max(0, Math.floor(Number(qtdPorCorLote[corKey]) || 0));
    const ladoMaior = maiorLadoMm(s.linha.medida);
    const metragemLinearTotal = ladoMaior > 0 ? ladoMaior * s.linha.qtd * qtdCor : 0;
    const metrosAPassar = s.linha.cabe > 0 ? metragemLinearTotal / s.linha.cabe : 0;
    const tempoSegundos = m60 > 0 ? (metrosAPassar * 60) / m60 : 0;
    const descricao = descricaoInstanciaParaRelatorio(bloco, id, s.linha.descricao);
    const tipoPintura = bloco.tipoPinturaPorInstanciaId?.[id] ?? s.linha.tipoPintura;
    return { ...s, linha: { ...s.linha, descricao, tipoPintura, tempoSegundos } };
  });
}

type LinhaComHorarioImpressao = LinhaCalculada & {
  instanciaId: string;
  inicio: Date | null;
  fim: Date | null;
};

function obterLinhasHorarioImpressao(
  bloco: BlocoLinhaPintura,
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[],
  lotesProdutos: LoteProdutoRow[],
  loteReferencia: string,
  metrosPor60Seg: number,
  qtdPorCorLote: Record<string, number>
): LinhaComHorarioImpressao[] {
  const seqDur = sequenciaComDuracoesParaRelatorio(
    bloco,
    produtosTempoCorteSalvos,
    lotesProdutos,
    loteReferencia,
    metrosPor60Seg,
    qtdPorCorLote
  );
  const excluirSet = new Set(bloco.excluirInstanciaIds || []);
  return aplicarHorarios(seqDur, excluirSet, bloco.horarioInicioPrimeira);
}

function distribuirLargurasColunasImpressao(linhasHorario: LinhaComHorarioImpressao[]) {
  const maxLenProduto = Math.max(1, ...linhasHorario.map((l) => String(l.nomeProduto || '').length));
  const maxLenDesc = Math.max(1, ...linhasHorario.map((l) => String(l.descricao || '').length));
  const maxLenMat = Math.max(1, ...linhasHorario.map((l) => String(l.material || '').length));
  const fixedQtde = 7;
  const fixedTipo = 5;
  const fixedIni = 11;
  const fixedFim = 11;
  const rest = Math.max(100 - (fixedQtde + fixedTipo + fixedIni + fixedFim), 30);
  const sumW = maxLenProduto + maxLenDesc + maxLenMat;
  const wProduto = Math.max(12, Math.round((rest * maxLenProduto) / sumW));
  const wDesc = Math.max(18, Math.round((rest * maxLenDesc) / sumW));
  const wMat = Math.max(rest - wProduto - wDesc, 8);
  return { wProduto, wDesc, wMat, fixedQtde, fixedTipo, fixedIni, fixedFim };
}

function gerarDocumentoHtmlRelatorioUnico(
  titulo: string,
  bloco: BlocoLinhaPintura,
  linhasHorario: LinhaComHorarioImpressao[],
  margemMm = 8
): string {
  const { wProduto, wDesc, wMat, fixedQtde, fixedTipo, fixedIni, fixedFim } =
    distribuirLargurasColunasImpressao(linhasHorario);
  const linhasHtml = linhasHorario
    .map(
      (l) => `<tr>
          <td>${escapeHtml(l.nomeProduto)}</td>
          <td>${escapeHtml(l.descricao)}</td>
          <td style="text-align:center">${l.cabe}</td>
          <td>${escapeHtml(l.material)}</td>
          <td style="text-align:center">${l.tipoPintura}</td>
          <td style="text-align:center;font-size:7pt">${l.inicio ? formatarHoraBR(l.inicio) : '—'}</td>
          <td style="text-align:center;font-size:7pt">${l.fim ? formatarHoraBR(l.fim) : '—'}</td>
        </tr>`
    )
    .join('');
  const rodapeHtml = `<div class="footer-grid">
      <div class="left">Início: ${escapeHtml(bloco.rodapeInicioHora || '____')} &nbsp; Data ${escapeHtml(
    bloco.rodapeInicioData || '____'
  )}</div>
      <div class="right">Término: ${escapeHtml(bloco.rodapeTerminoHora || '____')} &nbsp; Data ${escapeHtml(
    bloco.rodapeTerminoData || '____'
  )}</div>
    </div>`;
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escapeHtml(titulo)}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 9pt; }
  @page { size: A4 portrait; margin: ${margemMm}mm; }
  h1 { font-size: 12pt; margin: 0 0 2mm; }
  .sub { font-size: 9pt; color: #444; margin: 0 0 2mm; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th, td { border: 1px solid #334155; padding: 0.35mm 0.55mm; vertical-align: top; }
  th { background: #e0f2fe; font-size: 8.5pt; }
  td { overflow-wrap: anywhere; word-break: break-word; }
  tfoot td.tfoot { border-top: none; }
  .line { border-top: 1px solid #334155; margin: 2mm 0 2mm; }
  .footer-grid { display: grid; grid-template-columns: 1fr 1fr; font-size: 9pt; }
  .footer-grid .left { padding-right: 4mm; border-right: 1px solid #334155; }
  .footer-grid .right { padding-left: 4mm; }
</style></head><body>
  <h1>${escapeHtml(titulo)}</h1>
  <p class="sub"></p>
  <table>
    <colgroup>
      <col style="width:${wProduto}%">
      <col style="width:${wDesc}%">
      <col style="width:${fixedQtde}%">
      <col style="width:${wMat}%">
      <col style="width:${fixedTipo}%">
      <col style="width:${fixedIni}%">
      <col style="width:${fixedFim}%">
    </colgroup>
    <thead><tr>
      <th>Produto</th><th>Descrição</th><th>QTDE</th><th>Material</th><th>Tipo</th><th>Início</th><th>Término</th>
    </tr></thead>
    <tbody>${linhasHtml}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="7" class="tfoot">
          <div class="line"></div>
          ${rodapeHtml}
        </td>
      </tr>
    </tfoot>
  </table>
</body></html>`;
}

function montarSecaoHtmlRelatorioJunto(
  titulo: string,
  bloco: BlocoLinhaPintura,
  linhasHorario: LinhaComHorarioImpressao[],
  incluirRodape: boolean
): string {
  const { wProduto, wDesc, wMat, fixedQtde, fixedTipo, fixedIni, fixedFim } =
    distribuirLargurasColunasImpressao(linhasHorario);
  const linhasHtml = linhasHorario
    .map(
      (l) => `<tr>
              <td>${escapeHtml(l.nomeProduto)}</td>
              <td>${escapeHtml(l.descricao)}</td>
              <td style="text-align:center">${l.cabe}</td>
              <td>${escapeHtml(l.material)}</td>
              <td style="text-align:center">${l.tipoPintura}</td>
              <td style="text-align:center">${l.inicio ? formatarHoraBR(l.inicio) : '—'}</td>
              <td style="text-align:center">${l.fim ? formatarHoraBR(l.fim) : '—'}</td>
            </tr>`
    )
    .join('');

  const rodapeHtml = incluirRodape
    ? `<div class="footer-grid">
          <div class="left">Início: ${escapeHtml(bloco.rodapeInicioHora || '____')} &nbsp; Data ${escapeHtml(
          bloco.rodapeInicioData || '____'
        )}</div>
          <div class="right">Término: ${escapeHtml(bloco.rodapeTerminoHora || '____')} &nbsp; Data ${escapeHtml(
          bloco.rodapeTerminoData || '____'
        )}</div>
        </div>`
    : '';

  return `<section class="rep">
          <h1>${escapeHtml(titulo)}</h1>
          <p class="sub"></p>
          <table>
            <colgroup>
              <col style="width:${wProduto}%">
              <col style="width:${wDesc}%">
              <col style="width:${fixedQtde}%">
              <col style="width:${wMat}%">
              <col style="width:${fixedTipo}%">
              <col style="width:${fixedIni}%">
              <col style="width:${fixedFim}%">
            </colgroup>
            <thead><tr>
              <th>Produto</th><th>Descrição</th><th>QTDE</th><th>Material</th><th>Tipo</th><th>Início</th><th>Término</th>
            </tr></thead>
            <tbody>${linhasHtml}</tbody>
            ${
              rodapeHtml
                ? `<tfoot>
              <tr>
                <td colspan="7" class="tfoot">
                  <div class="line"></div>
                  ${rodapeHtml}
                </td>
              </tr>
            </tfoot>`
                : ''
            }
          </table>
        </section>`;
}

function montarSecaoHtmlRelatorioJuntoParaPng(
  titulo: string,
  bloco: BlocoLinhaPintura,
  linhasHorario: LinhaComHorarioImpressao[],
  incluirRodape: boolean,
  opts: { logoDataUrl: string | null; incluirLogoTopo: boolean }
): string {
  const { wProduto, wDesc, wMat, fixedQtde, fixedTipo, fixedIni, fixedFim } =
    distribuirLargurasColunasImpressao(linhasHorario);
  const linhasHtml = linhasHorario
    .map(
      (l) => `<tr>
              <td style="${PNG_TD}">${escapeHtml(l.nomeProduto)}</td>
              <td style="${PNG_TD}">${escapeHtml(l.descricao)}</td>
              <td style="${PNG_TD_C}">${l.cabe}</td>
              <td style="${PNG_TD}">${escapeHtml(l.material)}</td>
              <td style="${PNG_TD_C}">${l.tipoPintura}</td>
              <td style="${PNG_TD_C}">${l.inicio ? formatarHoraBR(l.inicio) : '—'}</td>
              <td style="${PNG_TD_C}">${l.fim ? formatarHoraBR(l.fim) : '—'}</td>
            </tr>`
    )
    .join('');

  const rodapeHtml = incluirRodape
    ? `<div style="display:grid;grid-template-columns:1fr 1fr;font-size:9pt;color:#0f172a;">
          <div style="padding-right:4mm;border-right:1px solid #334155;">Início: ${escapeHtml(
            bloco.rodapeInicioHora || '____'
          )} &nbsp; Data ${escapeHtml(bloco.rodapeInicioData || '____')}</div>
          <div style="padding-left:4mm;">Término: ${escapeHtml(bloco.rodapeTerminoHora || '____')} &nbsp; Data ${escapeHtml(
          bloco.rodapeTerminoData || '____'
        )}</div>
        </div>`
    : '';

  const logoBlock =
    opts.incluirLogoTopo && opts.logoDataUrl
      ? `<div style="margin-bottom:14px;text-align:center;line-height:0;">
           <img src="${opts.logoDataUrl.replace(/"/g, '&quot;')}" alt="" style="width:6cm;height:3cm;object-fit:contain;display:inline-block;vertical-align:top;" />
         </div>`
      : '';

  return `<section style="margin-bottom:16px;font-family:Arial,Helvetica,sans-serif;background:#fff;color:#0f172a;">
          ${logoBlock}
          <h1 style="font-size:12pt;margin:0 0 2mm;color:#0f172a;">${escapeHtml(titulo)}</h1>
          <p style="font-size:9pt;color:#444;margin:0 0 2mm;"></p>
          <table style="${PNG_TABLE}">
            <colgroup>
              <col style="width:${wProduto}%">
              <col style="width:${wDesc}%">
              <col style="width:${fixedQtde}%">
              <col style="width:${wMat}%">
              <col style="width:${fixedTipo}%">
              <col style="width:${fixedIni}%">
              <col style="width:${fixedFim}%">
            </colgroup>
            <thead><tr>
              <th style="${PNG_TH}">Produto</th><th style="${PNG_TH}">Descrição</th><th style="${PNG_TH}">QTDE</th><th style="${PNG_TH}">Material</th><th style="${PNG_TH}">Tipo</th><th style="${PNG_TH}">Início</th><th style="${PNG_TH}">Término</th>
            </tr></thead>
            <tbody>${linhasHtml}</tbody>
            ${
              rodapeHtml
                ? `<tfoot>
              <tr>
                <td colspan="7" style="border-top:none;border-left:1px solid #334155;border-right:1px solid #334155;border-bottom:1px solid #334155;padding:4px 6px;vertical-align:top;">
                  <div style="border-top:1px solid #334155;margin:2mm 0 2mm;"></div>
                  ${rodapeHtml}
                </td>
              </tr>
            </tfoot>`
                : ''
            }
          </table>
        </section>`;
}

function montarHtmlCorpoRelatoriosJuntos(
  blocos: BlocoLinhaPintura[],
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[],
  lotesProdutos: LoteProdutoRow[],
  loteReferencia: string,
  metrosPor60Seg: number,
  qtdPorCorLote: Record<string, number>
): string {
  return blocos
    .map((b, idx) => {
      const titulo = b.tituloProdutoCor || `Relatório ${idx + 1}`;
      const linhasHorario = obterLinhasHorarioImpressao(
        b,
        produtosTempoCorteSalvos,
        lotesProdutos,
        loteReferencia,
        metrosPor60Seg,
        qtdPorCorLote
      );
      if (linhasHorario.length === 0) return '';
      return montarSecaoHtmlRelatorioJunto(titulo, b, linhasHorario, idx === blocos.length - 1);
    })
    .filter(Boolean)
    .join('');
}

function montarHtmlCorpoRelatoriosJuntosParaPng(
  blocos: BlocoLinhaPintura[],
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[],
  lotesProdutos: LoteProdutoRow[],
  loteReferencia: string,
  metrosPor60Seg: number,
  qtdPorCorLote: Record<string, number>,
  logoDataUrl: string | null
): string {
  let jaMostrouLogo = false;
  return blocos
    .map((b, idx) => {
      const titulo = b.tituloProdutoCor || `Relatório ${idx + 1}`;
      const linhasHorario = obterLinhasHorarioImpressao(
        b,
        produtosTempoCorteSalvos,
        lotesProdutos,
        loteReferencia,
        metrosPor60Seg,
        qtdPorCorLote
      );
      if (linhasHorario.length === 0) return '';
      const incluirLogoTopo = Boolean(logoDataUrl && !jaMostrouLogo);
      if (incluirLogoTopo) jaMostrouLogo = true;
      return montarSecaoHtmlRelatorioJuntoParaPng(titulo, b, linhasHorario, idx === blocos.length - 1, {
        logoDataUrl,
        incluirLogoTopo,
      });
    })
    .filter(Boolean)
    .join('');
}

async function aguardarImagensCarregadas(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            })
    )
  );
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

function preferirWhatsAppWeb(): boolean {
  if (typeof navigator === 'undefined') return true;
  return /Windows|Macintosh|Linux|X11|CrOS/i.test(navigator.userAgent) && !/Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);
}

/** Só no celular: abre conversa com texto. No PC não usamos — nova aba do Web força login/QR de novo; o usuário usa a aba já aberta. */
function abrirWhatsappParaEnvioMobile(textoLegivel: string) {
  if (preferirWhatsAppWeb()) return;
  const t = encodeURIComponent(textoLegivel);
  window.open(`https://wa.me/?text=${t}`, '_blank', 'noopener,noreferrer');
}

async function compartilharOuBaixarPng(
  canvas: HTMLCanvasElement,
  nomeArquivo: string,
  textoWhatsapp: string
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error('PNG'));
          return;
        }
        const file = new File([blob], nomeArquivo, { type: 'image/png' });

        try {
          if (navigator.share && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: nomeArquivo, text: 'Relatório linha de pintura' });
            resolve();
            return;
          }
        } catch {
          /* cancelado */
        }

        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          abrirWhatsappParaEnvioMobile(textoWhatsapp);
          window.alert(
            preferirWhatsAppWeb()
              ? 'Imagem copiada. Vá para a aba do WhatsApp Web que já está aberta no Chrome e cole com Ctrl+V na conversa. (Não abrimos outra aba para não pedir o QR de novo.)'
              : 'Imagem copiada. Abra o WhatsApp e cole na conversa, ou use o botão que abriu.'
          );
          resolve();
          return;
        } catch {
          /* clipboard */
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nomeArquivo;
        a.click();
        URL.revokeObjectURL(url);
        abrirWhatsappParaEnvioMobile(textoWhatsapp);
        window.alert(
          preferirWhatsAppWeb()
            ? 'PNG baixado. Na aba do WhatsApp Web já aberta: anexe pelo clipe ou arraste o arquivo para a conversa.'
            : 'PNG baixado. Anexe no WhatsApp ou no app.'
        );
        resolve();
      },
      'image/png',
      1
    );
  });
}

async function exportarRelatorioPngWhatsapp(
  bloco: BlocoLinhaPintura,
  indice: number,
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[],
  lotesProdutos: LoteProdutoRow[],
  loteReferencia: string,
  metrosPor60Seg: number,
  qtdPorCorLote: Record<string, number>,
  logoDataUrl: string | null
): Promise<void> {
  const titulo = bloco.tituloProdutoCor || `Relatório ${indice}`;
  const linhasHorario = obterLinhasHorarioImpressao(
    bloco,
    produtosTempoCorteSalvos,
    lotesProdutos,
    loteReferencia,
    metrosPor60Seg,
    qtdPorCorLote
  );
  if (linhasHorario.length === 0) {
    window.alert('Não há linhas para exportar.');
    return;
  }

  const inner = montarSecaoHtmlRelatorioJuntoParaPng(titulo, bloco, linhasHorario, true, {
    logoDataUrl,
    incluirLogoTopo: Boolean(logoDataUrl),
  });
  const wrap = document.createElement('div');
  wrap.style.cssText =
    'position:fixed;left:-9999px;top:0;width:794px;padding:16px 20px;background:#fff;font-family:Arial,Helvetica,sans-serif;font-size:9pt;color:#111;';
  wrap.innerHTML = inner;
  document.body.appendChild(wrap);
  await aguardarImagensCarregadas(wrap);

  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(wrap, {
    scale: 3,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    height: wrap.scrollHeight,
    windowHeight: wrap.scrollHeight,
  });
  document.body.removeChild(wrap);

  const nomeArquivo = `linha-pintura-${String(indice).padStart(2, '0')}.png`;
  const textoWa = `${titulo}${loteReferencia ? ` — Lote ${loteReferencia}` : ''}`;
  await compartilharOuBaixarPng(canvas, nomeArquivo, textoWa);
}

async function exportarBlocosJuntosPngWhatsapp(
  blocos: BlocoLinhaPintura[],
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[],
  lotesProdutos: LoteProdutoRow[],
  loteReferencia: string,
  metrosPor60Seg: number,
  qtdPorCorLote: Record<string, number>,
  logoDataUrl: string | null
): Promise<void> {
  const inner = montarHtmlCorpoRelatoriosJuntosParaPng(
    blocos,
    produtosTempoCorteSalvos,
    lotesProdutos,
    loteReferencia,
    metrosPor60Seg,
    qtdPorCorLote,
    logoDataUrl
  );
  if (!inner.trim()) {
    window.alert('Não há linhas para exportar.');
    return;
  }

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'position:fixed;left:-9999px;top:0;width:794px;padding:16px 20px;background:#fff;font-family:Arial,Helvetica,sans-serif;font-size:9pt;color:#111;';
  wrap.innerHTML = inner;
  document.body.appendChild(wrap);
  await aguardarImagensCarregadas(wrap);

  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(wrap, {
    scale: 3,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    height: wrap.scrollHeight,
    windowHeight: wrap.scrollHeight,
  });
  document.body.removeChild(wrap);

  const nomeArquivo = `linha-pintura-todos-${new Date().toISOString().slice(0, 10)}.png`;
  const textoWa = `${blocos.length} relatório(s)${loteReferencia ? ` — Lote ${loteReferencia}` : ''}`;
  await compartilharOuBaixarPng(canvas, nomeArquivo, textoWa);
}

export default function BoletimLinhaFabrica({ produtosTempoCorteSalvos, lotesProdutos }: Props) {
  const [aba, setAba] = useState<'trabalho' | 'historico' | 'automatica'>('trabalho');
  const [loteReferencia, setLoteReferencia] = useState('');
  const [metrosPor60Seg, setMetrosPor60Seg] = useState(17);
  const [imprimirRelatoriosJuntos, setImprimirRelatoriosJuntos] = useState(false);
  const [exportandoWhatsappTodos, setExportandoWhatsappTodos] = useState(false);
  const [qtdPorCorLote, setQtdPorCorLote] = useState<Record<string, number>>({
    OFF: 0,
    CEDRO: 0,
    PRETO: 0,
    BRANCO: 0,
    NATURALE: 0,
    RIPADO: 0,
    OUTROS: 0,
  });
  const [blocos, setBlocos] = useState<BlocoLinhaPintura[]>([
    {
      id: novoIdBloco(),
      tituloProdutoCor: '',
      corLote: '',
      produtoIds: [],
      cabePorPeca: {},
      tipoPinturaPorPeca: {},
      excluirInstanciaIds: [],
      ordemInstancias: [],
      horarioInicioPrimeira: '',
    },
  ]);
  const [historico, setHistorico] = useState<RelatorioSalvo[]>([]);
  /** Evita gravar no localStorage antes de ler (senão o 1º save apaga os dados salvos com o estado inicial vazio). */
  const [storageHydrated, setStorageHydrated] = useState(false);
  const [logoWhatsappPng, setLogoWhatsappPng] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(LOGO_WHATSAPP_PNG_KEY);
    } catch {
      return null;
    }
  });

  const onEscolherLogoWhatsappPng = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !f.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const s = typeof reader.result === 'string' ? reader.result : null;
      if (!s) return;
      try {
        localStorage.setItem(LOGO_WHATSAPP_PNG_KEY, s);
        setLogoWhatsappPng(s);
      } catch {
        window.alert('Não foi possível guardar a imagem (muito grande para o navegador). Tente um arquivo menor.');
      }
    };
    reader.readAsDataURL(f);
  };

  const limparLogoWhatsappPng = () => {
    setLogoWhatsappPng(null);
    try {
      localStorage.removeItem(LOGO_WHATSAPP_PNG_KEY);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const v3 = localStorage.getItem('boletim-linha-pintura-v3');
        if (v3) {
          const p = JSON.parse(v3);
          const b = migrateBlocoForLoad({
            id: novoIdBloco(),
            tituloProdutoCor: '',
            produtoIds: Array.isArray(p.produtoIds) ? p.produtoIds.map(String) : [],
            cabePorPeca: p.cabePorPeca && typeof p.cabePorPeca === 'object' ? p.cabePorPeca : {},
            tipoPinturaPorPeca: {},
            excluirInstanciaIds: [],
            ordemInstancias: [],
            horarioInicioPrimeira:
              typeof p.horarioInicioPrimeira === 'string' ? p.horarioInicioPrimeira : '',
          });
          setBlocos([b]);
          if (typeof p.loteReferencia === 'string') setLoteReferencia(p.loteReferencia);
          if (typeof p.metrosPor60Seg === 'number') setMetrosPor60Seg(p.metrosPor60Seg);
        }
      } else {
        const p = JSON.parse(raw);
        if (typeof p.loteReferencia === 'string') setLoteReferencia(p.loteReferencia);
        if (typeof p.metrosPor60Seg === 'number') setMetrosPor60Seg(p.metrosPor60Seg);
        if (p.qtdPorCorLote && typeof p.qtdPorCorLote === 'object') {
          const next: Record<string, number> = {};
          for (const [k, v] of Object.entries(p.qtdPorCorLote as Record<string, unknown>)) {
            next[normalizar(k)] = Math.max(0, Math.floor(Number(v) || 0));
          }
          setQtdPorCorLote((prev) => ({ ...prev, ...next }));
        }
        if (Array.isArray(p.blocos) && p.blocos.length > 0) {
          setBlocos(
            p.blocos.map((b: unknown) =>
              migrateBlocoForLoad(b as Partial<BlocoLinhaPintura> & { qtdPorCor?: number })
            )
          );
        }
      }
    } catch {
      /* ignore */
    } finally {
      setStorageHydrated(true);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORICO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setHistorico(Array.isArray(parsed) ? parsed : []);
      }
    } catch {
      setHistorico([]);
    }
  }, []);

  useEffect(() => {
    if (!storageHydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ loteReferencia, metrosPor60Seg, qtdPorCorLote, blocos }));
    } catch {
      /* ignore */
    }
  }, [storageHydrated, loteReferencia, metrosPor60Seg, qtdPorCorLote, blocos]);

  const produtoIdsNoLote = useMemo(() => {
    const loteN = normalizar(loteReferencia);
    if (!loteN) return [];
    const nomesNoLote = new Set(
      (lotesProdutos || [])
        .filter((lp) => normalizar(lp.lote) === loteN)
        .map((lp) => normalizar(lp.produtoNome))
        .filter(Boolean)
    );
    const ids = (produtosTempoCorteSalvos || [])
      .filter((p) => nomesNoLote.has(normalizar(p.nome || '')))
      .map((p) => p.id);
    return Array.from(new Set(ids));
  }, [loteReferencia, lotesProdutos, produtosTempoCorteSalvos]);

  useEffect(() => {
    if (!storageHydrated) return;
    if (!normalizar(loteReferencia)) return;
    setBlocos((prev) => prev.map((b) => ({ ...b, produtoIds: produtoIdsNoLote })));
  }, [storageHydrated, loteReferencia, produtoIdsNoLote]);

  const atualizarBloco = (id: string, patch: Partial<BlocoLinhaPintura>) => {
    setBlocos((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const duplicarBloco = (bloco: BlocoLinhaPintura) => {
    setBlocos((prev) => {
      const idx = prev.findIndex((b) => b.id === bloco.id);
      const base = idx >= 0 ? prev[idx] : bloco;

      let novoInicio = base.horarioInicioPrimeira || '';
      try {
        const seqDur = sequenciaComDuracoesParaRelatorio(
          base,
          produtosTempoCorteSalvos,
          lotesProdutos,
          loteReferencia,
          metrosPor60Seg,
          qtdPorCorLote
        );
        const excluirSet = new Set(base.excluirInstanciaIds || []);
        const horarios = aplicarHorarios(seqDur, excluirSet, base.horarioInicioPrimeira);
        const lastFim = [...horarios].reverse().find((h) => h.fim)?.fim ?? null;
        if (lastFim) {
          const d = new Date(lastFim.getTime() + 20 * 60 * 1000);
          novoInicio = formatarHHMMSS(d);
        }
      } catch {
        // ignore
      }

      const novo: BlocoLinhaPintura = {
        ...base,
        id: novoIdBloco(),
        tituloProdutoCor: base.tituloProdutoCor ? `${base.tituloProdutoCor} (cópia)` : '',
        excluirInstanciaIds: [],
        ordemInstancias: base.ordemInstancias.map((o) => ({ id: novoIdInstancia(), rowKey: o.rowKey })),
        horarioInicioPrimeira: novoInicio,
      };
      return [...prev, novo];
    });
  };

  const removerBloco = (id: string) => {
    if (blocos.length <= 1) return;
    setBlocos((prev) => prev.filter((b) => b.id !== id));
  };

  const setCabeRow = (blocoId: string, rowKey: string, valor: number, map: Record<string, number>) => {
    const k = rowKey;
    atualizarBloco(blocoId, { cabePorPeca: { ...map, [k]: valor > 0 ? valor : 1 } });
  };

  const setTipoInstancia = (blocoId: string, instanciaId: string, tipo: TipoPintura) => {
    setBlocos((prev) =>
      prev.map((b) => {
        if (b.id !== blocoId) return b;
        return {
          ...b,
          tipoPinturaPorInstanciaId: { ...(b.tipoPinturaPorInstanciaId || {}), [instanciaId]: tipo },
        };
      })
    );
  };

  const toggleExcluirInstancia = (blocoId: string, instanciaId: string, atual: string[]) => {
    setBlocos((prev) =>
      prev.map((b) => {
        if (b.id !== blocoId) return b;
        const set = new Set(atual);
        const willExclude = !set.has(instanciaId);
        if (willExclude) set.add(instanciaId);
        else set.delete(instanciaId);
        return { ...b, excluirInstanciaIds: Array.from(set) };
      })
    );
  };

  const duplicarInstanciaLinha = (blocoId: string, indexVisivel: number) => {
    setBlocos((prev) =>
      prev.map((b) => {
        if (b.id !== blocoId) return b;
        const linhasBase = calcularLinhasBloco(
          b,
          produtosTempoCorteSalvos,
          lotesProdutos,
          loteReferencia,
          metrosPor60Seg,
          qtdPorCorLote
        );
        const seq = expandirInstanciasOrdem(
          linhasBase,
          b.ordemInstancias.length > 0 ? b.ordemInstancias : undefined
        );
        const excluirSet = new Set(b.excluirInstanciaIds);
        const vis = seq.filter((s) => !excluirSet.has(s.instanciaId));
        if (indexVisivel < 0 || indexVisivel >= vis.length) return b;
        const alvo = vis[indexVisivel];
        const fullIdx = seq.findIndex((s) => s.instanciaId === alvo.instanciaId);
        if (fullIdx < 0) return b;
        const novoId = novoIdInstancia();
        const novo = { id: novoId, rowKey: alvo.linha.rowKey };
        const ordem =
          b.ordemInstancias.length > 0
            ? [...b.ordemInstancias]
            : seq.map((s) => ({ id: s.instanciaId, rowKey: s.linha.rowKey }));
        ordem.splice(fullIdx + 1, 0, novo);
        return { ...b, ordemInstancias: ordem };
      })
    );
  };

  const salvarRelatorio = () => {
    const nome = window.prompt('Nome do relatório:', `Pintura ${loteReferencia || '—'} ${new Date().toLocaleDateString('pt-BR')}`);
    if (nome === null) return;
    const id = `${Date.now()}`;
    const item: RelatorioSalvo = {
      id,
      nome: nome.trim() || `Relatório ${id}`,
      criadoEmISO: new Date().toISOString(),
      loteReferencia,
      blocos: blocos.map((b) => ({ ...b })),
      metrosPor60Seg,
      qtdPorCorLote,
    };
    setHistorico((prev) => {
      const next = [item, ...prev];
      try {
        localStorage.setItem(HISTORICO_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const carregarRelatorio = (r: RelatorioSalvo) => {
    setLoteReferencia(r.loteReferencia);
    setMetrosPor60Seg(r.metrosPor60Seg);
    if (r.qtdPorCorLote && typeof r.qtdPorCorLote === 'object') {
      const next: Record<string, number> = {};
      for (const [k, v] of Object.entries(r.qtdPorCorLote as Record<string, unknown>)) {
        next[normalizar(k)] = Math.max(0, Math.floor(Number(v) || 0));
      }
      setQtdPorCorLote((prev) => ({ ...prev, ...next }));
    }
    if (r.blocos && r.blocos.length > 0) {
      setBlocos(
        r.blocos.map((b) =>
          migrateBlocoForLoad(b as Partial<BlocoLinhaPintura> & { qtdPorCor?: number })
        )
      );
    } else if (r.produtoIds && r.produtoIds.length > 0) {
      setBlocos([
        migrateBlocoForLoad({
          id: novoIdBloco(),
          tituloProdutoCor: '',
          produtoIds: [...r.produtoIds],
          cabePorPeca: r.cabePorPeca || {},
          tipoPinturaPorPeca: {},
          excluirInstanciaIds: [],
          ordemInstancias: [],
          horarioInicioPrimeira: r.horarioInicioPrimeira || '',
        }),
      ]);
    }
  };

  const excluirRelatorio = (id: string) => {
    if (!confirm('Remover este relatório do histórico?')) return;
    setHistorico((prev) => {
      const next = prev.filter((h) => h.id !== id);
      try {
        localStorage.setItem(HISTORICO_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const imprimirTodosRelatorios = () => {
    if (blocos.length === 0) return;
    const margem = 8;

    const blocosHtml = montarHtmlCorpoRelatoriosJuntos(
      blocos,
      produtosTempoCorteSalvos,
      lotesProdutos,
      loteReferencia,
      metrosPor60Seg,
      qtdPorCorLote
    );

    if (!blocosHtml) return;

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Relatórios</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 9pt; }
  @page { size: A4 portrait; margin: ${margem}mm; }
  h1 { font-size: 12pt; margin: 0 0 2mm; }
  .sub { font-size: 9pt; color: #444; margin: 0 0 2mm; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th, td { border: 1px solid #334155; padding: 0.35mm 0.55mm; vertical-align: top; }
  th { background: #e0f2fe; font-size: 8.5pt; }
  td { overflow-wrap: anywhere; word-break: break-word; }
  tfoot td.tfoot { border-top: none; }
  .line { border-top: 1px solid #334155; margin: 2mm 0 2mm; }
  .footer-grid { display: grid; grid-template-columns: 1fr 1fr; font-size: 9pt; }
  .footer-grid .left { padding-right: 4mm; border-right: 1px solid #334155; }
  .footer-grid .right { padding-left: 4mm; }
  section.rep { margin-bottom: 2.5mm; }
</style></head><body>
${blocosHtml}
</body></html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const exportarTodosWhatsappPng = async () => {
    if (blocos.length === 0) return;
    setExportandoWhatsappTodos(true);
    try {
      await exportarBlocosJuntosPngWhatsapp(
        blocos,
        produtosTempoCorteSalvos,
        lotesProdutos,
        loteReferencia,
        metrosPor60Seg,
        qtdPorCorLote,
        logoWhatsappPng
      );
    } finally {
      setExportandoWhatsappTodos(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-black flex items-center gap-2">
            <Droplets className="w-5 h-5 text-sky-600" /> Linha de pintura
          </h3>
          <p className="text-sm text-neutral-600 max-w-3xl">
            Peças iguais (descrição sem cor, medida, material) são unificadas somando a Qtd. Tipo e Cabe são por linha.
            Metragem/tempo por linha: m²/un. × Qtd (tempo corte) × Qtd por cor (por produto, pelo nome/ID no Tempo de
            Corte) × Qtd produtos no lote (soma Lotes; se não houver lote, usa 1). Arraste ⋮⋮ para reordenar; o botão ao
            lado do X duplica a linha logo abaixo (mesma peça outra vez na fila; término = início + tempo, depois o
            intervalo entre peças). Use
            &quot;Duplicar relatório&quot; para outra cor do mesmo produto. Intervalo entre peças: {GAP_SEG_ENTRE_PECAS}s.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={salvarRelatorio}
            disabled={blocos.every((b) => b.produtoIds.length === 0)}
            className="ade-btn-gold relative z-10 flex items-center gap-2 rounded-lg px-3 py-2 text-sm disabled:opacity-40"
          >
            <Save className="w-4 h-4" /> Salvar no histórico
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setAba('trabalho')}
          className={[
            'px-3 py-1.5 text-xs font-semibold rounded-lg border',
            aba === 'trabalho'
              ? 'border-[#009b3a] bg-[#e8f5ec] text-black ring-2 ring-[#009b3a]/40'
              : 'bg-[#fafbfc] text-black border-[#009b3a]/15 hover:bg-[#eef0f4]',
          ].join(' ')}
        >
          Trabalho
        </button>
        <button
          type="button"
          onClick={() => setAba('automatica')}
          className={[
            'px-3 py-1.5 text-xs font-semibold rounded-lg border',
            aba === 'automatica'
              ? 'border-[#009b3a] bg-[#e8f5ec] text-black ring-2 ring-[#009b3a]/40'
              : 'bg-[#fafbfc] text-black border-[#009b3a]/15 hover:bg-[#eef0f4]',
          ].join(' ')}
        >
          Automática
        </button>
        <button
          type="button"
          onClick={() => setAba('historico')}
          className={[
            'px-3 py-1.5 text-xs font-semibold rounded-lg border',
            aba === 'historico'
              ? 'border-amber-500 bg-amber-100 text-black ring-2 ring-amber-400/40'
              : 'bg-[#fafbfc] text-black border-[#009b3a]/15 hover:bg-[#eef0f4]',
          ].join(' ')}
        >
          Histórico ({historico.length})
        </button>
      </div>

      {aba === 'trabalho' && (
        <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-black">
              <input
                type="checkbox"
                checked={imprimirRelatoriosJuntos}
                onChange={(e) => setImprimirRelatoriosJuntos(e.target.checked)}
                className="rounded border-[#009b3a]/22"
              />
              Juntar relatórios para imprimir junto
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={!imprimirRelatoriosJuntos || blocos.length === 0}
                onClick={imprimirTodosRelatorios}
                className="ade-btn-gold relative z-10 flex items-center gap-2 rounded-lg px-3 py-2 text-xs disabled:opacity-40"
              >
                <Printer className="w-4 h-4" /> Imprimir todos
              </button>
              <button
                type="button"
                disabled={!imprimirRelatoriosJuntos || blocos.length === 0 || exportandoWhatsappTodos}
                onClick={() => void exportarTodosWhatsappPng()}
                title="Mesmo layout de «Imprimir todos» em PNG. No PC: copia a imagem — use a aba do WhatsApp Web já aberta e cole (Ctrl+V). No celular pode abrir o WhatsApp."
                className="flex items-center gap-2 rounded-lg border-2 border-[#25D366] bg-[#e8f8ec] px-3 py-2 text-xs font-semibold text-black hover:bg-[#d4f0e0] disabled:opacity-40"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={16} height={16} fill="#fff" aria-hidden>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                {exportandoWhatsappTodos ? 'Gerando PNG…' : 'WhatsApp PNG (todos)'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#009b3a]/10">
            <span className="text-xs font-semibold text-black">Imagem acima do PNG (WhatsApp)</span>
            <label className="inline-flex items-center">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onEscolherLogoWhatsappPng}
              />
              <span className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-[#009b3a]/22 bg-[#eef0f4] hover:bg-[#e4e6ec] cursor-pointer">
                Buscar no PC…
              </span>
            </label>
            {logoWhatsappPng && (
              <>
                <img
                  src={logoWhatsappPng}
                  alt=""
                  className="h-14 w-auto max-w-[5rem] object-contain border border-[#009b3a]/15 rounded bg-[#fafbfc]"
                />
                <button
                  type="button"
                  onClick={limparLogoWhatsappPng}
                  className="text-xs font-medium text-rose-600 hover:underline"
                >
                  Remover
                </button>
              </>
            )}
            <span className="text-[11px] text-neutral-600">
              Área fixa no PNG: 6&nbsp;cm de largura × 3&nbsp;cm de altura (mantém proporção com object-fit).
            </span>
          </div>
        </div>
      )}

      {aba === 'automatica' && (
        <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15 space-y-3">
          <div className="text-sm font-bold text-black">Linha de pintura automática</div>
          <p className="text-xs text-neutral-700">
            Digite o lote. Para <strong>cada cor</strong> com quantidade &gt; 0 nas caixas (aba Trabalho), o sistema procura no
            histórico o <strong>resumo daquela cor</strong> (cor padrão ou título com OFF, CEDRO, PRETO…) e gera{' '}
            <strong>um relatório com todos os produtos do lote</strong> que existem naquele modelo. Sem quantidade em nenhuma
            cor, gera um único relatório com o melhor resumo que encontrar.
          </p>
          <label className="block text-xs font-medium text-neutral-700">
            Referência do lote
            <input
              type="text"
              value={loteReferencia}
              onChange={(e) => setLoteReferencia(e.target.value.toUpperCase())}
              placeholder="Ex: 8674"
              className="mt-1 w-full max-w-[12rem] px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm font-semibold uppercase"
            />
          </label>
          <button
            type="button"
            onClick={() => {
              const ids = produtoIdsNoLote;
              if (ids.length === 0) {
                setBlocos((prev) => prev);
                return;
              }

              const tituloParaPids = (pids: string[], cor: string, modelo: BlocoLinhaPintura | undefined) => {
                const t = modelo?.tituloProdutoCor?.trim();
                if (t) return t;
                const nomes = pids
                  .map((id) => produtosTempoCorteSalvos.find((p) => p.id === id)?.nome || id)
                  .join(' + ');
                return cor ? `${nomes} ${cor}`.trim() : nomes;
              };

              const blocoVazio = (pids: string[], cor: string, titulo: string): BlocoLinhaPintura => ({
                ...migrateBlocoForLoad({ produtoIds: pids, corLote: cor }),
                id: novoIdBloco(),
                tituloProdutoCor: titulo,
                produtoIds: pids,
                excluirInstanciaIds: [],
                ordemInstancias: [],
                horarioInicioPrimeira: '',
                rodapeInicioHora: '',
                rodapeInicioData: '',
                rodapeTerminoHora: '',
                rodapeTerminoData: '',
              });

              let coresAtivas = Object.entries(qtdPorCorLote)
                .filter(([, v]) => (Number(v) || 0) > 0)
                .map(([k]) => k);

              const novos: BlocoLinhaPintura[] = [];

              if (coresAtivas.length === 0) {
                const modelo = encontrarBlocoModeloHistoricoParaCorDoLote('', ids, historico, produtosTempoCorteSalvos);
                const idsModelo = new Set((modelo?.produtoIds || []).map(String));
                const pidsParaBloco = ids.filter((pid) => idsModelo.has(String(pid)));
                const pids = pidsParaBloco.length > 0 ? pidsParaBloco : ids;
                const tit = tituloParaPids(pids, modelo?.corLote || '', modelo);
                if (modelo && pidsParaBloco.length > 0) {
                  novos.push(filtrarBlocoDoHistoricoParaProdutos(pidsParaBloco, modelo, tit));
                } else {
                  novos.push(blocoVazio(pids, '', tit));
                }
              } else {
                for (const cor of coresAtivas) {
                  const modelo = encontrarBlocoModeloHistoricoParaCorDoLote(
                    cor,
                    ids,
                    historico,
                    produtosTempoCorteSalvos
                  );
                  const idsModelo = new Set((modelo?.produtoIds || []).map(String));
                  const pidsParaBloco = ids.filter((pid) => idsModelo.has(String(pid)));
                  const tit = tituloParaPids(pidsParaBloco.length > 0 ? pidsParaBloco : ids, cor, modelo);
                  if (modelo && pidsParaBloco.length > 0) {
                    novos.push(filtrarBlocoDoHistoricoParaProdutos(pidsParaBloco, modelo, tit));
                  } else {
                    novos.push(blocoVazio(ids, cor, tit));
                  }
                }
              }

              setBlocos(novos.length ? novos : blocos);
              setAba('trabalho');
            }}
            className="ade-neu-card-sm rounded-lg px-3 py-2 text-xs font-semibold text-black hover:brightness-[1.03]"
          >
            Gerar relatórios do lote
          </button>
        </div>
      )}

      {aba === 'trabalho' && (
        <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs font-medium text-neutral-700">
          Referência do lote
          <input
            type="text"
            value={loteReferencia}
            onChange={(e) => setLoteReferencia(e.target.value.toUpperCase())}
            placeholder="Ex: 8651"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm font-semibold uppercase"
          />
        </label>
        <label className="text-xs font-medium text-neutral-700">
          Metros lineares por 60 s na linha
          <input
            type="number"
            step="0.1"
            min={0.1}
            value={metrosPor60Seg}
            onChange={(e) => setMetrosPor60Seg(Number(e.target.value) || 17)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
          />
        </label>
        </div>
      )}

      {aba === 'trabalho' && (
        <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
        <div className="text-xs font-semibold text-black mb-2">Quantidades do lote por cor</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
          {Object.keys(qtdPorCorLote)
            .sort((a, b) => a.localeCompare(b))
            .map((cor) => (
              <label key={cor} className="text-[11px] text-neutral-700">
                {cor}
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={qtdPorCorLote[cor] ?? 0}
                  onChange={(e) =>
                    setQtdPorCorLote((prev) => ({
                      ...prev,
                      [cor]: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                    }))
                  }
                  className="mt-1 w-full px-2 py-2 rounded-lg border border-[#009b3a]/22 text-sm tabular-nums font-semibold text-center"
                />
              </label>
            ))}
        </div>
        </div>
      )}

      {aba === 'historico' && (
        <div className="bg-[#fafbfc] rounded-xl p-4 border border-amber-200 border-dashed">
          <h4 className="font-semibold text-black flex items-center gap-2 mb-2">
            <History className="w-4 h-4" /> Histórico
          </h4>
          {historico.length === 0 ? (
            <p className="text-xs text-neutral-600">Nenhum relatório salvo.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {historico.map((h, idxHist) => {
                const salvNum = idxHist + 1;
                const blocosH = h.blocos && h.blocos.length > 0 ? h.blocos : [];
                const rotulosBlocos = blocosH.map((raw) => {
                  const m = migrateBlocoForLoad(raw as Partial<BlocoLinhaPintura> & { qtdPorCor?: number });
                  const t = String(m.tituloProdutoCor || '').trim();
                  const c = String(m.corLote || '').trim();
                  if (t) return t;
                  if (c) return `Cor ${c}`;
                  return 'Bloco';
                });
                const ids = Array.from(
                  new Set(
                    (blocosH.length > 0
                      ? blocosH.flatMap((b) => b.produtoIds || [])
                      : h.produtoIds || []
                    ).map(String)
                  )
                );
                const nomes = ids
                  .map((id) => produtosTempoCorteSalvos.find((p) => p.id === id)?.nome || id)
                  .filter(Boolean);
                const resumo = nomes.slice(0, 6).join(', ');
                const resto = nomes.length > 6 ? ` +${nomes.length - 6}` : '';
                return (
                  <li
                    key={h.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-1 border-b border-[#009b3a]/10 last:border-0"
                  >
                    <div className="min-w-[12rem]">
                      <div>
                        <span className="text-[10px] font-bold uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded mr-2">
                          Salvamento {salvNum}
                        </span>
                        <span className="font-medium text-black">{h.nome}</span>
                        <span className="text-neutral-600 text-xs ml-2">
                          {new Date(h.criadoEmISO).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      {rotulosBlocos.length > 0 && (
                        <div className="text-[11px] text-black mt-0.5">
                          <span className="font-semibold">{rotulosBlocos.length} resumo(s):</span>{' '}
                          {rotulosBlocos.join(' · ')}
                        </div>
                      )}
                      <div className="text-[11px] text-neutral-700">
                        <span className="font-semibold text-black">Produtos (união):</span> {resumo || '—'}
                        {resto}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => carregarRelatorio(h)}
                        className="rounded bg-[#e8f5ec] px-2 py-1 text-xs font-medium text-black hover:bg-[#d4f0e0]"
                      >
                        Carregar
                      </button>
                      <button
                        type="button"
                        onClick={() => excluirRelatorio(h.id)}
                        className="p-1 text-rose-600 hover:bg-rose-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {aba === 'trabalho' &&
        blocos.map((bloco, idxBloco) => (
          <BlocoTabela
            key={bloco.id}
            indice={idxBloco + 1}
            bloco={bloco}
            produtosTempoCorteSalvos={produtosTempoCorteSalvos}
            lotesProdutos={lotesProdutos}
            loteReferencia={loteReferencia}
            metrosPor60Seg={metrosPor60Seg}
            qtdPorCorLote={qtdPorCorLote}
            logoWhatsappPng={logoWhatsappPng}
            onChangeTitulo={(v) => atualizarBloco(bloco.id, { tituloProdutoCor: v })}
            onChangeCorLote={(v) => atualizarBloco(bloco.id, { corLote: v })}
            onChangeHorario={(v) => atualizarBloco(bloco.id, { horarioInicioPrimeira: v })}
            onPatchRodape={(patch) => atualizarBloco(bloco.id, patch)}
          onSetDescricao={(instanciaId, v) =>
            atualizarBloco(bloco.id, {
              descricaoOverridePorInstanciaId: { ...(bloco.descricaoOverridePorInstanciaId || {}), [instanciaId]: v },
            })
          }
          onSetCorLinha={(instanciaId, v) =>
            atualizarBloco(bloco.id, {
              corPorInstanciaId: { ...(bloco.corPorInstanciaId || {}), [instanciaId]: v },
            })
          }
          onSetQtdCorLinha={(instanciaId, v) =>
            atualizarBloco(bloco.id, {
              qtdCorOverridePorInstanciaId: {
                ...(bloco.qtdCorOverridePorInstanciaId || {}),
                [instanciaId]: v,
              },
            })
          }
            onSetCabe={(rowKey, v) => setCabeRow(bloco.id, rowKey, v, bloco.cabePorPeca)}
          onSetTipo={(instanciaId, t) => setTipoInstancia(bloco.id, instanciaId, t)}
            onToggleExcluir={(instanciaId) =>
              toggleExcluirInstancia(bloco.id, instanciaId, bloco.excluirInstanciaIds)
            }
            onAtualizarOrdemInstancias={(ordem) => atualizarBloco(bloco.id, { ordemInstancias: ordem })}
            onDuplicarInstanciaLinha={(indexVisivel) => duplicarInstanciaLinha(bloco.id, indexVisivel)}
            onDuplicar={() => duplicarBloco(bloco)}
            onRemover={() => removerBloco(bloco.id)}
            podeRemoverBloco={blocos.length > 1}
          />
        ))}
    </div>
  );
}

function BlocoTabela({
  indice,
  bloco,
  produtosTempoCorteSalvos,
  lotesProdutos,
  loteReferencia,
  metrosPor60Seg,
  qtdPorCorLote,
  logoWhatsappPng,
  onChangeTitulo,
  onChangeCorLote,
  onChangeHorario,
  onPatchRodape,
  onSetDescricao,
  onSetCorLinha,
  onSetQtdCorLinha,
  onSetCabe,
  onSetTipo,
  onToggleExcluir,
  onAtualizarOrdemInstancias,
  onDuplicarInstanciaLinha,
  onDuplicar,
  onRemover,
  podeRemoverBloco,
}: {
  indice: number;
  bloco: BlocoLinhaPintura;
  produtosTempoCorteSalvos: ProdutoTempoCorteSalvo[];
  lotesProdutos: LoteProdutoRow[];
  loteReferencia: string;
  metrosPor60Seg: number;
  qtdPorCorLote: Record<string, number>;
  logoWhatsappPng: string | null;
  onChangeTitulo: (v: string) => void;
  onChangeCorLote: (v: string) => void;
  onChangeHorario: (v: string) => void;
  onPatchRodape: (patch: Partial<Pick<BlocoLinhaPintura, 'rodapeInicioHora' | 'rodapeInicioData' | 'rodapeTerminoHora' | 'rodapeTerminoData'>>) => void;
  onSetDescricao: (instanciaId: string, v: string) => void;
  onSetCorLinha: (instanciaId: string, v: string) => void;
  onSetQtdCorLinha: (instanciaId: string, v: number) => void;
  onSetCabe: (rowKey: string, v: number) => void;
  onSetTipo: (instanciaId: string, t: TipoPintura) => void;
  onToggleExcluir: (instanciaId: string) => void;
  onAtualizarOrdemInstancias: (ordem: Array<{ id: string; rowKey: string }>) => void;
  onDuplicarInstanciaLinha: (indexVisivel: number) => void;
  onDuplicar: () => void;
  onRemover: () => void;
  podeRemoverBloco: boolean;
}) {
  const dragKeyRef = useRef<string | null>(null);
  const cabeInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const qtdCorInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [exportandoWhatsapp, setExportandoWhatsapp] = useState(false);

  const linhasBase = useMemo(
    () =>
      calcularLinhasBloco(bloco, produtosTempoCorteSalvos, lotesProdutos, loteReferencia, metrosPor60Seg, qtdPorCorLote),
    [bloco, produtosTempoCorteSalvos, lotesProdutos, loteReferencia, metrosPor60Seg, qtdPorCorLote]
  );

  const sequenciaComOverrides = useMemo(
    () =>
      sequenciaComDuracoesParaRelatorio(
        bloco,
        produtosTempoCorteSalvos,
        lotesProdutos,
        loteReferencia,
        metrosPor60Seg,
        qtdPorCorLote
      ),
    [bloco, produtosTempoCorteSalvos, lotesProdutos, loteReferencia, metrosPor60Seg, qtdPorCorLote]
  );

  const excluirSet = useMemo(() => new Set(bloco.excluirInstanciaIds), [bloco.excluirInstanciaIds]);

  const linhasVisiveis = useMemo(
    () => sequenciaComOverrides.filter((s) => !excluirSet.has(s.instanciaId)),
    [sequenciaComOverrides, excluirSet]
  );

  const linhasExcluidas = useMemo(
    () => sequenciaComOverrides.filter((s) => excluirSet.has(s.instanciaId)),
    [sequenciaComOverrides, excluirSet]
  );

  const linhasHorario = useMemo(
    () => aplicarHorarios(sequenciaComOverrides, excluirSet, bloco.horarioInicioPrimeira),
    [sequenciaComOverrides, excluirSet, bloco.horarioInicioPrimeira]
  );

  const totais = useMemo(() => {
    const vis = sequenciaComOverrides.filter((s) => !excluirSet.has(s.instanciaId));
    return {
      tempo: vis.reduce((s, x) => s + x.linha.tempoSegundos, 0),
    };
  }, [sequenciaComOverrides, excluirSet]);

  const imprimirBloco = () => {
    if (linhasHorario.length === 0) return;
    const margem = 8;
    const titulo = bloco.tituloProdutoCor || `Relatório ${indice}`;
    const html = gerarDocumentoHtmlRelatorioUnico(titulo, bloco, linhasHorario, margem);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const enviarWhatsappPng = async () => {
    if (linhasHorario.length === 0) return;
    setExportandoWhatsapp(true);
    try {
      await exportarRelatorioPngWhatsapp(
        bloco,
        indice,
        produtosTempoCorteSalvos,
        lotesProdutos,
        loteReferencia,
        metrosPor60Seg,
        qtdPorCorLote,
        logoWhatsappPng
      );
    } finally {
      setExportandoWhatsapp(false);
    }
  };

  return (
    <div className="bg-[#fafbfc] rounded-xl border-2 border-sky-200 p-4 space-y-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-bold text-sky-800">Relatório {indice}</span>
        <div className="flex flex-wrap gap-2">
          {linhasExcluidas.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const last = bloco.excluirInstanciaIds[bloco.excluirInstanciaIds.length - 1];
                if (last) onToggleExcluir(last);
              }}
              className="px-3 py-1.5 bg-[#e4e6ec] hover:bg-[#dde0e6] text-black text-xs font-semibold rounded-lg"
              title="Restaura a última linha excluída"
            >
              Desfazer exclusão
            </button>
          )}
          <button
            type="button"
            onClick={imprimirBloco}
            disabled={linhasHorario.length === 0}
            className="ade-btn-gold relative z-10 flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs disabled:opacity-40"
          >
            <Printer className="w-3.5 h-3.5" /> Imprimir
          </button>
          <button
            type="button"
            onClick={() => void enviarWhatsappPng()}
            disabled={linhasHorario.length === 0 || exportandoWhatsapp}
            title="PNG em alta resolução. No PC não abre nova aba do WhatsApp (evita QR): copie e cole na aba Web já aberta."
            className="flex items-center gap-1.5 rounded-lg border-2 border-[#25D366] bg-[#e8f8ec] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#d4f0e0] disabled:opacity-40"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={16} height={16} fill="#fff" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {exportandoWhatsapp ? 'Gerando…' : 'WhatsApp (PNG)'}
          </button>
          <button
            type="button"
            onClick={onDuplicar}
            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-semibold rounded-lg flex items-center gap-1"
          >
            <Copy className="w-3.5 h-3.5" /> Duplicar relatório
          </button>
          {podeRemoverBloco && (
            <button
              type="button"
              onClick={onRemover}
              className="px-2 py-1.5 text-rose-600 hover:bg-rose-50 text-xs rounded-lg"
            >
              Remover bloco
            </button>
          )}
        </div>
      </div>

      <label className="block text-xs font-medium text-neutral-700">
        Nome do produto + cor (título deste relatório)
        <input
          type="text"
          value={bloco.tituloProdutoCor}
          onChange={(e) => onChangeTitulo(e.target.value)}
          placeholder='Ex: RACK ARUBA OFF'
          className="mt-1 w-full px-3 py-2 rounded-lg border border-sky-300 text-sm font-semibold"
        />
      </label>

      <label className="block text-xs font-medium text-neutral-700">
        Início da 1ª peça (horário)
        <input
          type="time"
          step={1}
          value={bloco.horarioInicioPrimeira || ''}
          onChange={(e) => onChangeHorario(e.target.value)}
          className="mt-1 w-full max-w-[10rem] px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
        />
      </label>

      <div className="border border-[#009b3a]/15 rounded-lg p-3 bg-[#eef0f4]/70">
        <div className="text-xs font-semibold text-black mb-2">Rodapé da impressão (preencha aqui)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-neutral-700">
              Início (HH:MM)
              <input
                type="text"
                value={bloco.rodapeInicioHora || ''}
                onChange={(e) => onPatchRodape({ rodapeInicioHora: e.target.value })}
                placeholder="00:00"
                className="mt-1 w-full px-2 py-1.5 rounded-lg border border-[#009b3a]/22 text-sm font-semibold"
              />
            </label>
            <label className="text-[11px] text-neutral-700">
              Data
              <input
                type="text"
                value={bloco.rodapeInicioData || ''}
                onChange={(e) => onPatchRodape({ rodapeInicioData: e.target.value })}
                placeholder="00/00/00"
                className="mt-1 w-full px-2 py-1.5 rounded-lg border border-[#009b3a]/22 text-sm font-semibold"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-neutral-700">
              Término (HH:MM)
              <input
                type="text"
                value={bloco.rodapeTerminoHora || ''}
                onChange={(e) => onPatchRodape({ rodapeTerminoHora: e.target.value })}
                placeholder="00:00"
                className="mt-1 w-full px-2 py-1.5 rounded-lg border border-[#009b3a]/22 text-sm font-semibold"
              />
            </label>
            <label className="text-[11px] text-neutral-700">
              Data
              <input
                type="text"
                value={bloco.rodapeTerminoData || ''}
                onChange={(e) => onPatchRodape({ rodapeTerminoData: e.target.value })}
                placeholder="00/00/00"
                className="mt-1 w-full px-2 py-1.5 rounded-lg border border-[#009b3a]/22 text-sm font-semibold"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-xs font-medium text-neutral-700">
          Cor padrão do relatório (cada linha pode sobrescrever)
          <select
            value={bloco.corLote || ''}
            onChange={(e) => onChangeCorLote(e.target.value)}
            className="mt-1 w-full max-w-[12rem] px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm font-semibold"
          >
            <option value="">— selecione —</option>
            {Object.keys(qtdPorCorLote)
              .sort((a, b) => a.localeCompare(b))
              .map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
          </select>
        </label>
        <div className="text-xs text-neutral-700">
          <span className="font-semibold text-black block">Produtos carregados do lote</span>
          {!normalizar(loteReferencia) ? (
            <span className="text-neutral-600">Digite a referência do lote para carregar automaticamente.</span>
          ) : bloco.produtoIds.length === 0 ? (
            <span className="text-amber-700">Nenhum produto do lote foi encontrado no Tempo de Corte.</span>
          ) : (
            <span className="text-black">
              {bloco.produtoIds.length} produto(s) —{' '}
              {bloco.produtoIds
                .map((id) => produtosTempoCorteSalvos.find((p) => p.id === id)?.nome || id)
                .filter(Boolean)
                .join(', ')}
            </span>
          )}
        </div>
      </div>

      {linhasExcluidas.length > 0 && (
        <details className="text-[11px] border border-dashed border-[#009b3a]/22 rounded-lg p-2 bg-[#eef0f4]/70">
          <summary className="cursor-pointer font-semibold text-black">
            Linhas excluídas ({linhasExcluidas.length}) — clique para restaurar
          </summary>
          <div className="flex flex-wrap gap-2 mt-2">
            {linhasExcluidas.map((s) => (
              <button
                key={s.instanciaId}
                type="button"
                onClick={() => onToggleExcluir(s.instanciaId)}
                className="px-2 py-1 bg-[#fafbfc] border border-[#009b3a]/15 rounded text-xs text-left hover:bg-[#e4e6ec] max-w-full"
                title="Restaurar esta linha"
              >
                <span className="font-semibold text-black">{s.linha.nomeProduto}</span>
                <span className="text-neutral-700"> — {s.linha.descricao}</span>
              </button>
            ))}
          </div>
        </details>
      )}

      {(bloco.ordemInstancias?.length ?? 0) > 0 && linhasVisiveis.length > 1 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onAtualizarOrdemInstancias([])}
            className="text-[11px] text-sky-700 hover:text-sky-900 underline"
          >
            Restaurar ordem alfabética
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-[#009b3a]/15 rounded-lg">
        <table className="w-full text-[11px] border-collapse min-w-[980px]">
          <thead>
            <tr className="bg-sky-100">
              <th colSpan={12} className="border border-[#009b3a]/22 px-2 py-2 text-left bg-sky-200">
                <span className="text-neutral-700 font-normal">Título: </span>
                <span className="font-bold text-black">{bloco.tituloProdutoCor || '— (preencha acima)'}</span>
                <span className="text-neutral-700 font-normal ml-3">Cor do lote: </span>
                <span className="font-bold text-black">{bloco.corLote || '—'}</span>
              </th>
            </tr>
            <tr className="bg-sky-100">
              <th
                className="border border-[#009b3a]/22 px-0 py-1 w-7 text-center"
                title="Arrastar para reordenar"
              >
                <GripVertical className="w-3.5 h-3.5 mx-auto text-neutral-600" aria-hidden />
              </th>
              <th
                className="border border-[#009b3a]/22 px-0.5 py-1 w-[4.25rem] text-center"
                title="Excluir linha / Duplicar linha abaixo"
              >
                <span className="sr-only">Ações</span>
              </th>
              <th className="border border-[#009b3a]/22 px-1 py-1 text-left">Produto</th>
              <th className="border border-[#009b3a]/22 px-0.5 py-1 w-11 text-center">Tipo</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 text-left">Descrição</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 text-left">Material</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 text-left">Medida</th>
              <th className="border border-[#009b3a]/22 px-0.5 py-1 w-12 text-center">Cor</th>
              <th className="border border-[#009b3a]/22 px-0.5 py-1 w-14 text-center">Qtd cor</th>
              <th className="border border-[#009b3a]/22 px-0.5 py-1 w-10 text-center">Qtd</th>
              <th className="border border-[#009b3a]/22 px-0.5 py-1 w-12 text-center">Cabe</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 text-right">Tempo</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 text-left whitespace-nowrap">Início</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 text-left whitespace-nowrap">Término</th>
            </tr>
          </thead>
          <tbody>
            {linhasVisiveis.map((s, idxVis) => {
              const l = s.linha;
              const lh = linhasHorario.find((x) => x.instanciaId === s.instanciaId);
              return (
                <tr
                  key={s.instanciaId}
                  className={[draggingKey === s.instanciaId ? 'opacity-60' : '', 'hover:bg-[#eef0f4]']
                    .filter(Boolean)
                    .join(' ')}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromKey = e.dataTransfer.getData('text/plain') || dragKeyRef.current;
                    if (!fromKey || fromKey === s.instanciaId) return;
                    const ids = linhasVisiveis.map((x) => x.instanciaId);
                    const from = ids.indexOf(fromKey);
                    const to = ids.indexOf(s.instanciaId);
                    if (from < 0 || to < 0) return;
                    const newIds = moverRowKeyNaLista(ids, from, to);
                    const visReordenados = newIds
                      .map((id) => linhasVisiveis.find((v) => v.instanciaId === id))
                      .filter(Boolean) as InstanciaLinha[];
                    const novaOrdem = mergeOrdemAposArrastar(sequenciaComOverrides, excluirSet, visReordenados);
                    onAtualizarOrdemInstancias(novaOrdem);
                    dragKeyRef.current = null;
                    setDraggingKey(null);
                  }}
                >
                  <td className="border border-[#009b3a]/15 px-0 w-7 text-center align-middle">
                    <span
                      draggable
                      aria-label="Arrastar para reordenar a linha"
                      title="Arrastar para reordenar"
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', s.instanciaId);
                        e.dataTransfer.effectAllowed = 'move';
                        dragKeyRef.current = s.instanciaId;
                        setDraggingKey(s.instanciaId);
                      }}
                      onDragEnd={() => {
                        dragKeyRef.current = null;
                        setDraggingKey(null);
                      }}
                      className="inline-flex cursor-grab active:cursor-grabbing text-neutral-600 hover:text-sky-600 p-0.5 rounded"
                    >
                      <GripVertical className="w-4 h-4" />
                    </span>
                  </td>
                  <td className="border border-[#009b3a]/15 px-0.5 text-center align-middle">
                    <div className="flex items-center justify-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => onToggleExcluir(s.instanciaId)}
                        className="p-0.5 text-rose-600 hover:bg-rose-50 rounded shrink-0"
                        title="Excluir linha do relatório"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDuplicarInstanciaLinha(idxVis)}
                        className="rounded p-0.5 text-[#009b3a] hover:bg-[#f0faf3] shrink-0"
                        title="Criar uma cópia desta linha logo abaixo (mesmo tempo; horários recalculados)"
                      >
                        <CopyPlus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="border border-[#009b3a]/15 px-1 py-0.5 font-semibold break-words">{l.nomeProduto}</td>
                  <td className="border border-[#009b3a]/15 px-0.5 py-0.5 text-center">
                    <select
                      value={bloco.tipoPinturaPorInstanciaId?.[s.instanciaId] ?? l.tipoPintura}
                      onChange={(e) => onSetTipo(s.instanciaId, e.target.value as TipoPintura)}
                      className="w-9 max-w-[2.75rem] px-0 py-0.5 text-center text-[10px] font-bold border border-[#009b3a]/22 rounded"
                    >
                      <option value="G">G</option>
                      <option value="B">B</option>
                      <option value="A">A</option>
                    </select>
                  </td>
                  <td className="border border-[#009b3a]/15 px-1 py-0.5">
                    <input
                      type="text"
                      value={l.descricao}
                      onChange={(e) => onSetDescricao(s.instanciaId, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === ' ') {
                          e.stopPropagation();
                        }
                      }}
                      className="w-full px-1 py-0.5 text-[11px] border border-[#009b3a]/15 rounded bg-[#fafbfc]"
                    />
                  </td>
                  <td className="border border-[#009b3a]/15 px-1 py-0.5 break-words">{l.material}</td>
                  <td className="border border-[#009b3a]/15 px-1 py-0.5">{l.medida}</td>
                  <td className="border border-[#009b3a]/15 px-0.5 py-0.5 text-center">
                    <select
                      value={bloco.corPorInstanciaId?.[s.instanciaId] ?? bloco.corLote ?? ''}
                      onChange={(e) => onSetCorLinha(s.instanciaId, e.target.value)}
                      className="w-12 px-0 py-0.5 text-center text-[10px] font-bold border border-[#009b3a]/22 rounded"
                    >
                      <option value="">—</option>
                      {Object.keys(qtdPorCorLote)
                        .sort((a, b) => a.localeCompare(b))
                        .map((k) => (
                          <option key={k} value={k}>
                            {k}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="border border-[#009b3a]/15 px-0.5 py-0.5 text-center tabular-nums">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={bloco.qtdCorOverridePorInstanciaId?.[s.instanciaId] ?? 0}
                      onChange={(e) =>
                        onSetQtdCorLinha(s.instanciaId, Math.max(0, Math.floor(Number(e.target.value) || 0)))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const next = idxVis + 1;
                          if (next < linhasVisiveis.length) {
                            const el = qtdCorInputRefs.current[next];
                            el?.focus();
                            el?.select();
                          }
                        }
                      }}
                      ref={(el) => {
                        qtdCorInputRefs.current[idxVis] = el;
                      }}
                      title="0 = usa a quantidade das caixas acima"
                      className="w-full max-w-[3.25rem] mx-auto px-0.5 py-0.5 text-center text-[10px] border border-[#009b3a]/22 rounded"
                    />
                  </td>
                  <td className="border border-[#009b3a]/15 px-0.5 py-0.5 text-center tabular-nums w-10">{l.qtd}</td>
                  <td className="border border-[#009b3a]/15 px-0.5 py-0.5 text-center w-12">
                    <input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={l.cabe}
                      onChange={(e) => onSetCabe(l.rowKey, Number(e.target.value) || 1)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const next = idxVis + 1;
                          if (next < linhasVisiveis.length) {
                            const el = cabeInputRefs.current[next];
                            el?.focus();
                            el?.select();
                          }
                        }
                      }}
                      ref={(el) => {
                        cabeInputRefs.current[idxVis] = el;
                      }}
                      className="w-full max-w-[2.75rem] mx-auto px-0.5 py-0.5 text-center text-[10px] border border-[#009b3a]/22 rounded"
                    />
                  </td>
                  <td className="border border-[#009b3a]/15 px-1 py-0.5 text-right font-mono text-[10px]">
                    {formatarSegundosHHMMSS(l.tempoSegundos)}
                  </td>
                  <td className="border border-[#009b3a]/15 px-1 py-0.5 text-[9px] whitespace-nowrap">
                    {lh?.inicio ? formatarHoraBR(lh.inicio) : '—'}
                  </td>
                  <td className="border border-[#009b3a]/15 px-1 py-0.5 text-[9px] whitespace-nowrap">
                    {lh?.fim ? formatarHoraBR(lh.fim) : '—'}
                  </td>
                </tr>
              );
            })}
            {linhasVisiveis.length > 0 && (
              <tr className="bg-[#e4e6ec] font-semibold">
                <td colSpan={11} className="border border-[#009b3a]/22 px-2 py-1 text-right">
                  Totais (linhas visíveis)
                </td>
                <td className="border border-[#009b3a]/22 px-1 py-1 text-right font-mono text-[10px]">
                  {formatarSegundosHHMMSS(totais.tempo)}
                </td>
                <td colSpan={2} className="border border-[#009b3a]/22" />
              </tr>
            )}
            {linhasBase.length === 0 && (
              <tr>
                <td colSpan={12} className="border px-4 py-6 text-center text-neutral-600">
                  Digite a referência do lote para carregar as peças.
                </td>
              </tr>
            )}
            {linhasBase.length > 0 && linhasVisiveis.length === 0 && (
              <tr>
                <td colSpan={12} className="border px-4 py-6 text-center text-amber-800 bg-amber-50/50">
                  Todas as linhas estão excluídas. Use a lista acima para restaurar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
